import { debugLog } from "./utils/debugLog.js";
import React, { Fragment, Suspense, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import CommandPalette from './components/CommandPalette.jsx';
import ShortcutsModal from './components/ShortcutsModal.jsx';
import CompanyProfileSearchPalette from './components/CompanyProfileSearchPalette.jsx';
import { getCompanySectionNavItems } from './CompanyProfileFields.js';
import {
  OPEN_COMPANY_PROFILE_SEARCH_EVENT,
  buildCompanyProfileSearchUrl,
  dispatchCompanyProfileSearchFocus,
  dispatchCompanyProfileFieldFocus,
  dispatchCompanyProfileCallFocus,
  dispatchCompanyProfileOnboardingFocus,
  focusCompanyProfileField,
  focusCompanyProfileCallRecording,
} from './utils/companyProfileSearch.js';
import PalmTreeIcon from './components/PalmTreeIcon.jsx';
import DualBootBanner from './components/DualBootBanner.jsx';
import AddGrantModal from './components/AddGrantModal.jsx';
import GrantOpportunityModal from './components/GrantOpportunityModal.jsx';
import { OPPORTUNITY_STATUS_OPTIONS } from './utils/opportunityStatusOptions.js';
import 'react-loading-skeleton/dist/skeleton.css';
import Skeleton from 'react-loading-skeleton';
import {
  Bars3Icon,
  PhoneIcon,
  Cog6ToothIcon,
  HomeIcon,
  XMarkIcon,
  BoltIcon,
  LightBulbIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  GiftIcon,
  TrophyIcon,
  IdentificationIcon,
  FolderIcon,
  GlobeAmericasIcon,
  UserGroupIcon,
  FireIcon,
  ChartBarSquareIcon,
  UserPlusIcon,
  BellIcon,
  EnvelopeIcon,
  CloudArrowDownIcon,
  CogIcon,
  PaperAirplaneIcon,
  ArrowRightStartOnRectangleIcon,
  PlusIcon,
  CircleStackIcon,
  EllipsisHorizontalIcon,
  TableCellsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ShieldCheckIcon,
  BookOpenIcon,
  MapIcon,
  QuestionMarkCircleIcon,
  ReceiptRefundIcon,
  ReceiptPercentIcon,
  BeakerIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  ArrowUpCircleIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  MagnifyingGlassCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/20/solid';
import { Dialog, Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { UserButton, useUser, useClerk, useAuth } from '@clerk/clerk-react';
import { setOpportunityAuditContext } from './utils/opportunityAuditClient.js';
import './utils/instrumentOpportunityWrites.js';
import Airtable from 'airtable';
import { AppContext } from './AppContext.js';
import Notification from './Notification.js';
import { ONBOARDING_TASKS_CONFIG } from './config/onboardingTasks.js';
import OnboardingChecklistWidget from './components/OnboardingChecklistWidget.jsx';
import {
  classNames,
  getQueryVariable,
  setShowNotif,
  setContents,
  showNotification,
  checkEmail,
  extractDomain,
  getWebURL,
  checkValidDomainName,
  isAdminUser,
} from './helpers.js';
import Progress from './Progress.js';
import KeyMetricsNav from './components/KeyMetricsNav.jsx';
import { postLeadBarSlackFromBrowser } from './utils/leadBarSlackNotification.js';
import { sendLeadBarWelcomeEmail } from './utils/leadBarWelcomeEmail.js';

const debugLayout = process.env.REACT_APP_DEBUG_VERBOSE === 'true';

// Routes where Composio/QuickBooks is actually used (Connect button, vendor report, integrations)
// Composio is lazy-loaded only on these routes - not on /home, /grants-section, etc.
const COMPOSIO_ROUTES = ['/software-savings', '/settings', '/company-info-section', '/onboarding', '/onboarding-section', '/onboarding-v2', '/company-earnings-estimate'];
// Tooltip Component
const Tooltip = ({ children, content, show, className = 'inline-flex' }) => {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (show && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const parentRect = tooltipRef.current.parentElement.getBoundingClientRect();

      // Position tooltip to the right of the icon
      setPosition({
        top: parentRect.top + (parentRect.height / 2) - 2, // Center vertically, moved down slightly more
        left: parentRect.right + 8, // 8px to the right
      });
    }
  }, [show]);

  return (
    <div className={classNames('relative', className)}>
      {children}
      {show && (
        <div
          ref={tooltipRef}
          className="fixed z-50 px-3 py-2 text-xs font-medium text-gray-800 bg-neutral-100 border border-neutral-200 rounded-lg shadow-lg pointer-events-none transition-opacity duration-200"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translateY(-50%)',
          }}
        >
          {content}
          {/* Arrow pointing left */}
          <div className="absolute top-1/2 left-0 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-neutral-100 transform -translate-x-1 -translate-y-1/2"></div>
        </div>
      )}
    </div>
  );
};

const PageSkeleton = () => (
  <div className="px-6 py-8" aria-busy="true" aria-live="polite">
    <span className="sr-only">Loading page</span>
    <div className="animate-pulse space-y-6">
      <div className="space-y-2">
        <div className="h-5 w-48 rounded bg-gray-100" />
        <div className="h-3 w-72 rounded bg-gray-100" />
      </div>
      <div className="space-y-4">
        {[0, 1, 2].map((row) => (
          <div key={row} className="space-y-3 rounded-xl border border-gray-100 bg-white p-6">
            <div className="h-4 w-1/3 rounded bg-gray-100" />
            <div className="h-3 w-2/3 rounded bg-gray-100" />
            <div className="h-3 w-1/2 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const base = new Airtable({ apiKey: apiKey }).base(baseId);

const OPPORTUNITIES_TABLE = 'ALLGRANTS&SAVINGS(Automated-AccountsReceivable)';

const OPPORTUNITY_MODAL_DEFAULT_FORM = {
  assignee: '',
  companyName: '',
  companyID: '',
  opportunityName: '',
  amount: '',
  status: 'Value Roadmap',
  notes: '',
  estCompletionDate: '',
  estHearBackDate: '',
};

// Personal email domains that require workspace selection
const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.ca',
  'yahoo.co.uk',
  'outlook.com',
  'hotmail.com',
  'hotmail.ca',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'mail.com',
  'zoho.com',
];

// Check if email domain is a personal/consumer domain
const isPersonalEmail = (email) => {
  if (!email) return true;
  const domain = extractDomain(email);
  return PERSONAL_EMAIL_DOMAINS.includes(domain?.toLowerCase());
};

const navigationNew = [
  { 
    name: 'Home', 
    href: '/', 
    icon: HomeIcon, 
    current: false, 
    pro: false,
    isPage: true,
  },
  {
    name: 'Profile & Company',
    href: '/company-info-section',
    icon: IdentificationIcon,
    current: false,
    pro: false,
    isPage: true,
  },
  {
    name: 'Grants Roadmap',
    href: '/grants-section',
    icon: MapIcon,
    current: false,
    pro: true,
    isPage: true,
    shortcut: 'j',
  },
  {
    name: 'Grants Finder',
    href: '/all-grants',
    icon: GiftIcon,
    current: false,
    pro: true,
    isPage: true,
  },
  {
    name: 'Software Savings',
    href: '/software-savings',
    icon: ReceiptPercentIcon,
    current: false,
    pro: true,
    isPage: true,
  },
  {
    name: 'AI Automations',
    href: '/ai-automations',
    icon: BoltIcon,
    current: false,
    pro: true,
    isPage: true,
    shortcut: 'i',
  },
  {
    name: 'Financing',
    href: '/financing',
    icon: BanknotesIcon,
    current: false,
    pro: false,
    isPage: true,
    hideWhenCollapsed: true,
    shortcut: 'b',
  },
  {
    name: 'Receive Referrals & Earn ✌️',
    href: '#my-network',
    icon: GlobeAmericasIcon,
    current: false,
    pro: false,
    isPage: true,
  },
  {
    name: 'My Reports',
    href: '/my-reports',
    icon: FolderIcon,
    current: false,
    pro: true,
    isPage: true,
  },
  {
    name: 'My Upgrades',
    href: '#my-upgrades',
    icon: ChartBarSquareIcon,
    current: false,
    pro: false,
    admin: true
  },
  {
    name: 'Onboarding',
    href: '/onboarding-section',
    icon: ClipboardDocumentListIcon,
    current: false,
    pro: false,
    isPage: true,
  },
];

const teamAdminSections = [
  {
    name: 'Guides & Docs',
    items: [
      {
        name: 'Team Guides',
        href: '/admin/team-docs',
        icon: BookOpenIcon,
        matchPrefix: '/admin/team-docs',
      },
    ],
  },
  {
    name: 'Clients & Accounts',
    items: [
      {
        name: 'Customers Admin',
        href: '/customers-admin',
        icon: UserGroupIcon,
      },
      {
        name: 'Opportunity Ledger Log',
        href: '/admin/ar-ledger-log',
        icon: ClockIcon,
      },
      {
        name: 'View Workspace as User',
        href: '/workspace-selection',
        icon: UserGroupIcon,
        preserveSearch: false,
        onClick: (e, navigate) => {
          e.preventDefault();
          window.scrollTo({ top: 0 });
          navigate('/workspace-selection', { state: { openAdminSwitcher: true } });
        },
      },
      {
        name: 'Company Info',
        href: '/company-info',
        matchPrefix: '/company-info',
        icon: BuildingOffice2Icon,
        onClick: (e, navigate, currentSearch) => {
          e.preventDefault();
          window.scrollTo({ top: 0 });
          const params = new URLSearchParams(
            (currentSearch || window.location.search || '').replace(/^\?/, '')
          );
          const website = params.get('website');
          if (!website) {
            // No company context — fall back to Profile & Company settings
            navigate(`/company-info-section${currentSearch || ''}`);
            return;
          }
          const domain = decodeURIComponent(website).split('&')[0];
          navigate(`/company-info/${domain}${currentSearch || ''}`);
        },
      },
    ],
  },
  {
    name: 'Grants & Strategy',
    items: [
      {
        name: 'Grant Admin',
        href: '/grants-admin',
        icon: TableCellsIcon,
      },
      {
        name: 'PerfectDraft Creator',
        href: '/edit-grant-questions',
        icon: ClipboardDocumentListIcon,
      },
      {
        name: 'SR&ED Claims',
        href: '/sred',
        matchPrefix: '/sred',
        icon: BeakerIcon,
      },
      {
        name: 'Client Leaderboard',
        href: '/leaderboard',
        icon: FireIcon,
      },
    ],
  },
  {
    name: 'Business Development',
    items: [
      {
        name: 'Prospect Leaderboard',
        href: '/prospect-leaderboard',
        icon: FireIcon,
      },
      {
        name: 'Automation Requests',
        href: '/automation-requests',
        icon: BoltIcon,
      },
      {
        name: 'Financing Requests',
        href: '/financing-requests',
        icon: BanknotesIcon,
      },
      {
        name: 'AI Automations',
        href: '/ai-automations',
        icon: BoltIcon,
        shortcut: 'i',
      },
    ],
  },
  {
    name: 'Software Savings',
    items: [
      {
        name: 'Vendor Savings Admin',
        href: '/vendor-savings-admin',
        icon: CurrencyDollarIcon,
      },
    ],
  },
  {
    name: 'Performance & Reports',
    items: [
      {
        name: 'Team Earnings',
        href: '/#team-pipeline',
        icon: UserPlusIcon,
        onClick: (e, navigate, currentSearch) => {
          e.preventDefault();
          const targetId = 'team-pipeline';
          const element = document.getElementById(targetId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
          navigate(`/${currentSearch}#team-pipeline`);
        },
      },
      {
        name: 'My Reports',
        href: '#my-reports',
        icon: ChartBarSquareIcon,
        onClick: (e, navigate, currentSearch) => {
          e.preventDefault();
          navigate(`/${currentSearch}#my-reports`);
          setTimeout(() => {
            const element = document.getElementById('my-reports');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        },
      },
      {
        name: 'Leaderboard',
        href: '/public-leaderboard',
        icon: TrophyIcon,
      },
    ],
  },
];

const userNavigation = teamAdminSections.flatMap((section) => section.items);

const adminQuickLinks = [
  {
    name: 'Partners & Community',
    href: '/companies',
    icon: BuildingOffice2Icon,
    shortcut: 'u',
    adminOnly: true,
  },
  {
    name: 'SR&ED',
    href: '/sred',
    matchPrefix: '/sred',
    icon: BeakerIcon,
  },
];

const adminNavLinks = [
  {
    name: 'All Opportunities',
    href: '/all-opportunities',
    icon: PalmTreeIcon,
  },
];

const navShortcutLabel = (letter) => (letter ? `⌘${String(letter).toUpperCase()}` : '');

const navTooltip = (item) => (
  item?.shortcut ? `${item.name} (${navShortcutLabel(item.shortcut)})` : item?.name
);

function NavShortcutHint({ letter }) {
  if (!letter) return null;
  return (
    <span className="ml-auto inline-flex shrink-0 items-center rounded-md border border-gray-200 bg-white px-1.5 py-0.5 font-sans text-[11px] leading-none text-gray-400">
      {navShortcutLabel(letter)}
    </span>
  );
}

const SIDEBAR_SUB_NAVS = {
  admin: {
    title: 'Team Admin',
    icon: ShieldCheckIcon,
    sections: teamAdminSections,
    items: userNavigation,
  },
};

const renderSidebarSubNavSections = (sections, { isSubNavItemActive, handleSubNavClick, onAfterClick, compact = false }) => {
  const itemClassName = compact ? 'gap-2.5 px-3 py-2' : 'gap-x-2.5 px-3 py-1.5';

  return (
    <div className="space-y-1">
      {sections.map((section, sectionIndex) => (
        <div key={section.name}>
          {sectionIndex > 0 && <div className="my-1.5 border-t border-gray-100" aria-hidden="true" />}
          <p className="px-3 pb-0.5 pt-1 text-[10px] font-normal text-gray-400">
            {section.name}
          </p>
          <ul role="list" className="space-y-0.5">
            {section.items
              .filter((item) => item)
              .map((item) => (
                <li key={item.name}>
                  <button
                    type="button"
                    onClick={(e) => {
                      handleSubNavClick(e, item);
                      onAfterClick?.();
                    }}
                    className={`group flex w-full items-center text-left text-xs transition-colors ${itemClassName} ${
                      isSubNavItemActive(item)
                        ? 'bg-gray-100 font-medium text-gray-900'
                        : 'font-normal text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {item.icon && (
                      <item.icon
                        className={`h-4 w-4 shrink-0 ${
                          isSubNavItemActive(item)
                            ? 'text-gray-900'
                            : 'text-gray-400 group-hover:text-gray-900'
                        }`}
                        aria-hidden="true"
                      />
                    )}
                    <span className="min-w-0 flex-1 truncate">{item.name}</span>
                    <NavShortcutHint letter={item.shortcut} />
                  </button>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

// Helper function to extract base company name from domain
const extractBaseCompanyName = (domain) => {
  if (!domain) return '';

  // Remove protocol, www, and get the hostname
  let cleanDomain = domain.toLowerCase();

  // Remove protocol
  if (cleanDomain.includes('://')) {
    cleanDomain = cleanDomain.split('://')[1];
  }

  // Remove www
  if (cleanDomain.startsWith('www.')) {
    cleanDomain = cleanDomain.substring(4);
  }

  // Remove path, query params, etc.
  if (cleanDomain.includes('/')) {
    cleanDomain = cleanDomain.split('/')[0];
  }

  // Split by dots and get the main part (before TLD)
  const parts = cleanDomain.split('.');
  if (parts.length >= 2) {
    // Return the main domain part (e.g., "hootsuite" from "hootsuite.com" or "hootsuite.ca")
    return parts[0];
  }

  return cleanDomain;
};

const Layout = ({ }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Helper function to check if a navigation item is active
  const isActive = (href) => {
    if (!href) return false;
    // Handle hash links (like #settings, #my-network)
    if (href.startsWith('#')) {
      // For hash links, check if we're on the home page and the hash matches
      return location.pathname === '/' && location.hash === href;
    }
    // For page routes, check if pathname matches
    // Handle exact match for root
    if (href === '/') {
      return location.pathname === '/';
    }
    // Handle other routes - exact match
    return location.pathname === href;
  };

  // Collapsed sidebar: square icon hit-target with soft grey active plate (see Slim reference)
  const collapsedIconBtnClass = (active) =>
    classNames(
      'group flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors duration-150',
      active
        ? 'bg-gray-100 text-gray-900'
        : 'text-gray-400 hover:bg-gray-50 hover:text-gray-900'
    );

  // Query string for nav/section links: preserve URL params, or build from company context, or empty for anonymous.
  // When admin views another company's report (e.g. ?website=althra.ca&company=Althra), all section links keep that context.
  const getNavSearch = () => {
    const search = location.search;
    const hasWebsite = search && search.includes('website=');
    if (hasWebsite) {
      // URL already has both params — use as-is so admin stays in that company's context
      if (search.includes('company=')) return search;
      // URL has website but no company — add company from context when it matches the URL domain
      if (company?.fields?.Domain) {
        try {
          const params = new URLSearchParams(search.replace(/^\?/, ''));
          const websiteFromUrl = params.get('website');
          if (websiteFromUrl && company.fields.Domain === websiteFromUrl) {
            const name = company.fields.Name || 'Company';
            return `?website=${encodeURIComponent(company.fields.Domain)}&company=${encodeURIComponent(name)}`;
          }
        } catch (_) { /* ignore */ }
      }
      return search;
    }
    if (company?.fields?.Domain) {
      const domain = company.fields.Domain;
      const name = company.fields.Name || 'Company';
      return `?website=${encodeURIComponent(domain)}&company=${encodeURIComponent(name)}`;
    }
    return '';
  };

  const pathForNavItem = (item, href = item.href) => {
    const pagePath = href.startsWith('#') ? href.replace('#', '/') : href;
    if (item?.preserveSearch === false) return pagePath;
    return `${pagePath}${getNavSearch()}`;
  };

  const { signOut } = useClerk();
  const { getToken } = useAuth();
  const { user: clerkUserFromHook, isLoaded: isClerkLoaded } = useUser();
  const [inviteOpen, setInviteOpen] = useState(false);
  const {
    company,
    companies,
    clerkUser,
    setClerkUser,
    domainStatus,
    setDomainStatus,
    setOpen,
    loading,
    setLoading,
    grantLoading,
    setGrantLoading,
    JITWorking,
    setJITWorking,
    unknownDomain,
    setUnknownDomain,
    setCompany,
    vendorSavingsActions,
  } = useContext(AppContext);

  // Ensure clerkUser is set in context when available
  useEffect(() => {
    if (isClerkLoaded && clerkUserFromHook && !clerkUser) {
      setClerkUser(clerkUserFromHook);
    }
  }, [isClerkLoaded, clerkUserFromHook, clerkUser, setClerkUser]);

  // Use clerkUser from context, fallback to hook if context isn't set yet
  const currentClerkUser = clerkUser || clerkUserFromHook;

  useEffect(() => {
    const email =
      currentClerkUser?.primaryEmailAddress?.emailAddress ||
      currentClerkUser?.emailAddresses?.[0]?.emailAddress ||
      '';
    const name = [currentClerkUser?.firstName, currentClerkUser?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    setOpportunityAuditContext({
      email,
      name: name || email,
      path: location.pathname,
      getToken,
    });
  }, [currentClerkUser, getToken, location.pathname]);

  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(
    checkEmail(email) ? 'correct' : email === '' ? 'empty' : 'incorrect'
  );
  const [domain, setDomain] = useState(extractDomain(''));
  const [admin, setAdmin] = useState(false);
  const [sidebarSubNav, setSidebarSubNav] = useState(null);
  const sidebarSubNavDismissedRef = useRef({ admin: false });
  const [isPartOfWorkspace, setIsPartOfWorkspace] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [companyProfileSearchOpen, setCompanyProfileSearchOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isAddGrantModalOpen, setIsAddGrantModalOpen] = useState(false);
  const [opportunityModal, setOpportunityModal] = useState({
    open: false,
    mode: 'add',
    opportunity: null,
    selectedGrant: null,
    formData: { ...OPPORTUNITY_MODAL_DEFAULT_FORM },
  });
  const [opportunityModalGrants, setOpportunityModalGrants] = useState([]);
  const [opportunityModalCompanies, setOpportunityModalCompanies] = useState([]);
  const [opportunityModalGrantsLoaded, setOpportunityModalGrantsLoaded] = useState(false);
  const [opportunityModalCompaniesLoaded, setOpportunityModalCompaniesLoaded] = useState(false);
  const [opportunityModalSelectedCompany, setOpportunityModalSelectedCompany] = useState(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const openSidebarSubNav = (key) => {
    sidebarSubNavDismissedRef.current[key] = false;
    setSidebarSubNav(key);
    if (sidebarCollapsed) setSidebarCollapsed(false);
  };

  const closeSidebarSubNav = () => {
    if (sidebarSubNav) {
      sidebarSubNavDismissedRef.current[sidebarSubNav] = true;
    }
    setSidebarSubNav(null);
  };

  const isSubNavItemActive = (item) => {
    if (!item?.href) return false;
    const href = item.href;
    if (item.matchPrefix) {
      return (
        location.pathname === item.matchPrefix ||
        location.pathname.startsWith(`${item.matchPrefix}/`)
      );
    }
    if (href.includes('#')) {
      const hash = href.startsWith('/#') ? href.slice(1) : (href.startsWith('#') ? href : `#${href.split('#')[1]}`);
      return location.pathname === '/' && location.hash === hash;
    }
    const path = href.startsWith('/') ? href : `/${href}`;
    return location.pathname === path;
  };

  const handleSubNavClick = (e, item) => {
    if (item.onClick) {
      item.onClick(e, navigate, getNavSearch());
      return;
    }
    e.preventDefault();
    window.scrollTo({ top: 0 });
    const search = item.preserveSearch === false ? '' : getNavSearch();
    navigate(`${item.href}${search}`);
  };

  // Referral Notification
  const [emailModalNotif, setEmailModalNotif] = useState({
    showNotif: false,
    contents: { type: '', heading: '', message: '' },
  });

  // Enrich Notification
  const [submitEnrichNotif, setSubmitEnrichNotif] = useState({
    showNotif: false,
    contents: { type: '', heading: '', message: '' },
  });

  const [notificationCount, setNotificationCount] = useState(0);

  // Add sidebarCollapsed state at the top of your Layout component
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarUtilsOpen, setSidebarUtilsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Function to check if company profile is complete (all basic info up to Profitable?)
  // Enhanced to handle Stripe payment and ManagedClient cases
  const isCompanyProfileComplete = () => {
    if (!company?.fields) return false;

    const requiredFields = [
      'Name',
      'Domain',
      'Employees',
      'Address',
      'Country',
      'ProvinceOrState',
      'Industry',
      'Founded',
      'Profitable?'
    ];

    const basicFieldsComplete = requiredFields.every(field => {
      const value = company.fields[field];
      return value !== null && value !== undefined && value !== '';
    });

    // For payment: check PaymentStatus OR ValidCreditCard OR ManagedClient
    // Managed clients may have paid via another endpoint
    const paymentComplete = 
      company.fields.PaymentStatus === 'Completed' ||
      company.fields.ValidCreditCard === true ||
      company.fields.ValidCreditCard === 'true' ||
      company.fields.ManagedClient === 'true';

    // Terms agreement is required (either via consent step or payment)
    const termsComplete = 
      company.fields.TermsAgreement ||
      company.fields.TermsOfService === true ||
      paymentComplete; // Payment implies terms acceptance

    return basicFieldsComplete && paymentComplete && termsComplete;
  };

  // Helper function to check QuickBooks connection (for task 2)
  // Composio is lazy-loaded only on COMPOSIO_ROUTES - avoids loading on /home, /grants-section, etc.
  // Tradeoff: checklist on /home won't show task 2 complete for QBO-only users until they visit /software-savings
  const isQuickBooksConnected = async () => {
    if (!currentClerkUser?.primaryEmailAddress?.emailAddress) return false;
    const pathname = location?.pathname || '/';
    if (!COMPOSIO_ROUTES.includes(pathname)) return false;
    try {
      const composioService = (await import('./services/composioService.js')).default;
      const response = await composioService.getConnections(currentClerkUser.primaryEmailAddress.emailAddress);
      return response.connections?.items?.length > 0;
    } catch (error) {
      console.error('[OnboardingChecklist] Error checking QBO connection:', error);
      return false;
    }
  };

  // Task 2: Upload & Redeem Tech Savings - CSV upload OR QBO connection
  const isTechSavingsUploaded = async () => {
    if (!company?.fields?.Domain) return false;

    // Check expenses first (no Composio) - CSV upload path
    try {
      const expensesRes = await fetch(
        `https://api.airtable.com/v0/${baseId}/Expenses?filterByFormula=${encodeURIComponent(`{Domain} = '${company.fields.Domain}'`)}&maxRecords=1`,
        { headers: { Authorization: `Bearer ${apiKey}` } }
      );
      const expensesJson = await expensesRes.json();
      if (expensesJson.records?.length > 0) return true;
    } catch (error) {
      console.error('[OnboardingChecklist] Error checking expenses:', error);
    }

    // Check if QBO is connected (only on Composio routes - avoids rate limiting)
    const qboConnected = await isQuickBooksConnected();
    return qboConnected;
  };

  // Task 3: Upload Proforma (Next 12 Mo Expenses) - TopExpenses field
  const isProformaUploaded = () => {
    return !!(company?.fields?.TopExpenses && 
              company.fields.TopExpenses.trim() !== '');
  };

  // Task 4: Draft & Apply to 5 Grants - InterestedIn field
  const hasFiveGrantsAdded = () => {
    const interestedIn = company?.fields?.InterestedIn || '';
    if (!interestedIn) return false;
    const grants = interestedIn.split(',').map(g => g.trim()).filter(Boolean);
    return grants.length >= 5;
  };

  // Task 5: Try 3 AI Automations - PrioritizedAutomationIds
  const hasThreeAutomationsHearted = () => {
    let ids = company?.fields?.PrioritizedAutomationIds;
    if (!ids) return false;
    if (typeof ids === 'string') {
      try {
        ids = JSON.parse(ids);
      } catch {
        return false;
      }
    }
    return Array.isArray(ids) && ids.length >= 3;
  };

  // Task 6: Try 3 Revenue Upgrades - PrioritizedAutomationIds with Category Filter
  const hasThreeRevenueUpgradesHearted = async () => {
    let ids = company?.fields?.PrioritizedAutomationIds;
    if (!ids) return false;
    if (typeof ids === 'string') {
      try {
        ids = JSON.parse(ids);
      } catch {
        return false;
      }
    }
    if (!Array.isArray(ids) || ids.length < 3) return false;
    
    // Fetch automation records to check categories
    try {
      const automationRecords = await Promise.all(
        ids.slice(0, 3).map(id => 
          base('automationsAIGrowthUpgrades').find(id).catch(() => null)
        )
      );
      
      const revenueCategories = ['Sales', 'Marketing', 'Revenue', 'Sales & Marketing'];
      const revenueCount = automationRecords.filter(record => {
        if (!record) return false;
        const category = record.fields?.Category || record.fields?.category || '';
        return revenueCategories.some(rc => 
          category.toLowerCase().includes(rc.toLowerCase())
        );
      }).length;
      
      return revenueCount >= 3;
    } catch (error) {
      console.error('[OnboardingChecklist] Error checking revenue upgrades:', error);
      return false;
    }
  };

  // Task 7: Request 1 Ideal Client Intro - IntroRequested field
  const hasRequestedIntro = () => {
    return company?.fields?.IntroRequested === 'true' || 
           company?.fields?.IntroRequested === true;
  };

  // Task 8: Refer & Earn More - Check Referrals table
  const hasAddedReferral = async () => {
    if (!currentClerkUser?.primaryEmailAddress?.emailAddress) return false;
    const userEmail = currentClerkUser.primaryEmailAddress.emailAddress;
    
    try {
      // Check Referrals table (where referrals are stored)
      const referralsRes = await fetch(
        `https://api.airtable.com/v0/${baseId}/Referrals?filterByFormula=${encodeURIComponent(`{UploadedBy} = '${userEmail}'`)}&maxRecords=1`,
        { headers: { Authorization: `Bearer ${apiKey}` } }
      );
      const referralsJson = await referralsRes.json();
      return referralsJson.records?.length > 0;
    } catch (error) {
      console.error('[OnboardingChecklist] Error checking referrals:', error);
      return false;
    }
  };

  useEffect(() => {
    if (!currentClerkUser) {
      setAdmin(false);
      setIsPartOfWorkspace(false);
      return;
    }

    const emailAddress = currentClerkUser.primaryEmailAddress?.emailAddress;
    if (!emailAddress) {
      setAdmin(false);
      setIsPartOfWorkspace(false);
      return;
    }

    setEmail(emailAddress);
    setAdmin(isAdminUser(currentClerkUser));
    setDomain(extractDomain(emailAddress));
  }, [currentClerkUser]);

  useEffect(() => {
    if (!admin) {
      setSidebarSubNav(null);
      sidebarSubNavDismissedRef.current = { admin: false };
      return;
    }
    const onAdminPage = userNavigation.some(isSubNavItemActive);
    if (!onAdminPage) {
      sidebarSubNavDismissedRef.current = { admin: false };
      return;
    }
    if (onAdminPage && !sidebarSubNavDismissedRef.current.admin) {
      setSidebarSubNav('admin');
    }
  }, [admin, location.pathname, location.hash]);

  // Check if user is part of the current workspace
  useEffect(() => {
    const checkWorkspaceMembership = async () => {
      if (!currentClerkUser || !company) {
        setIsPartOfWorkspace(false);
        return;
      }

      // If admin, they have access
      if (admin) {
        setIsPartOfWorkspace(true);
        return;
      }

      try {
        const emailAddress = currentClerkUser.primaryEmailAddress?.emailAddress;
        if (!emailAddress) {
          setIsPartOfWorkspace(false);
          return;
        }

        // Fetch user record from Airtable
        const records = await base('users')
          .select({
            filterByFormula: `{Email} = '${emailAddress}'`,
            maxRecords: 1
          })
          .all();

        if (records && records.length > 0) {
          const userRecord = records[0];
          const companiesField = userRecord.fields?.Companies;
          
          if (companiesField) {
            let companyIds = [];
            
            // Handle both array and string formats
            if (Array.isArray(companiesField)) {
              // Check if array contains objects (linked records) or strings/IDs
              if (companiesField.length > 0 && typeof companiesField[0] === 'object') {
                // Linked records - extract IDs from objects
                companyIds = companiesField.map(record => {
                  return String(record.id || record).trim();
                }).filter(id => id);
              } else {
                // Array of IDs (strings)
                companyIds = companiesField.map(id => String(id).trim()).filter(id => id);
              }
            } else if (typeof companiesField === 'string') {
              // If it's a string, split by comma
              companyIds = companiesField.split(',').map(id => id.trim()).filter(id => id);
            }
            
            // Check if current company ID is in the list
            const companyIdStr = String(company.id);
            const isMember = companyIds.some(id => String(id) === companyIdStr);
            setIsPartOfWorkspace(isMember);
          } else {
            setIsPartOfWorkspace(false);
          }
        } else {
          setIsPartOfWorkspace(false);
        }
      } catch (error) {
        console.error('[Layout] Error checking workspace membership:', error);
        setIsPartOfWorkspace(false);
      }
    };

    checkWorkspaceMembership();
  }, [currentClerkUser, company, admin]);

  // Redirect personal email users without companies to workspace selection
  useEffect(() => {
    const checkPersonalEmailRedirect = async () => {
      // Only check if user is loaded and signed in
      if (!isClerkLoaded || !currentClerkUser) {
        return;
      }

      // Exclude certain routes from this check
      const excludedRoutes = [
        '/workspace-selection',
        '/onboarding',
        '/onboarding-v2',
        '/company-earnings-estimate',
        '/roadmap-action',
        '/onboarding-payment',
        '/sign-in',
        '/sign-up',
        '/free-signup',
        '/forgot-password',
        '/check_oauth_google',
        '/sso-callback',
      ];

      const currentPath = location.pathname;
      const searchParams = new URLSearchParams(location.search);
      
      // Exclude routes that are in the excluded list
      if (excludedRoutes.some(route => currentPath.startsWith(route))) {
        return;
      }
      
      // Exclude if onboarding=true param is present (user just completed onboarding)
      if (searchParams.get('onboarding') === 'true') {
        if (debugLayout) debugLog('[Layout] Skipping redirect check - onboarding=true param present');
        return;
      }

      try {
        const emailAddress = currentClerkUser.primaryEmailAddress?.emailAddress;
        if (!emailAddress) {
          return;
        }

        // Check if user has a personal email
        if (!isPersonalEmail(emailAddress)) {
          // Not a personal email, no redirect needed
          return;
        }

        // Personal email user - check if they have any companies
        const airtableUserId = currentClerkUser.publicMetadata?.airtable_id || currentClerkUser.publicMetadata?.airtableId;
        
        let hasCompanies = false;
        let companyIds = [];

        // First check Clerk metadata
        if (currentClerkUser.publicMetadata?.company_id) {
          hasCompanies = true;
          companyIds = [currentClerkUser.publicMetadata.company_id];
        }

        // If not in metadata, check Airtable directly
        if (!hasCompanies) {
          try {
            // Try to find user by Airtable ID first
            if (airtableUserId) {
              try {
                const userRecord = await base('Users').find(airtableUserId);
                const companiesField = userRecord.fields['Companies'] || [];
                
                // Handle different formats of Companies field
                if (Array.isArray(companiesField) && companiesField.length > 0) {
                  hasCompanies = true;
                  // Extract company IDs
                  if (typeof companiesField[0] === 'object') {
                    companyIds = companiesField.map(record => String(record.id || record).trim()).filter(id => id);
                  } else {
                    companyIds = companiesField.map(id => String(id).trim()).filter(id => id);
                  }
                } else if (typeof companiesField === 'string' && companiesField.trim() !== '') {
                  hasCompanies = true;
                  companyIds = companiesField.split(',').map(id => id.trim()).filter(id => id);
                }
              } catch (idErr) {
                // If user not found by ID, try by email
                if (debugLayout) debugLog('[Layout] User not found by ID, trying by email');
              }
            }

            // If still no companies found, try by email
            if (!hasCompanies) {
              const records = await base('Users')
                .select({
                  filterByFormula: `LOWER({Email}) = '${emailAddress.toLowerCase()}'`,
                  maxRecords: 1,
                })
                .firstPage();

              if (records.length > 0) {
                const userRecord = records[0];
                const companiesField = userRecord.fields['Companies'] || [];
                
                if (Array.isArray(companiesField) && companiesField.length > 0) {
                  hasCompanies = true;
                  // Extract company IDs
                  if (typeof companiesField[0] === 'object') {
                    companyIds = companiesField.map(record => String(record.id || record).trim()).filter(id => id);
                  } else {
                    companyIds = companiesField.map(id => String(id).trim()).filter(id => id);
                  }
                } else if (typeof companiesField === 'string' && companiesField.trim() !== '') {
                  hasCompanies = true;
                  companyIds = companiesField.split(',').map(id => id.trim()).filter(id => id);
                }
              }
            }
          } catch (err) {
            console.error('[Layout] Error checking user companies:', err);
          }
        }

        // If personal email user has no companies, redirect to workspace selection
        if (!hasCompanies) {
          if (debugLayout) debugLog('[Layout] Personal email user without companies, redirecting to workspace-selection');
          navigate('/workspace-selection');
          return;
        }

        // Personal email user HAS companies - check if onboarding is complete
        if (hasCompanies && companyIds.length > 0) {
          try {
            // Get the first company (primary company)
            const primaryCompanyId = companyIds[0];
            
            // Always fetch fresh data from Airtable to avoid stale context data
            // This ensures we get the latest OnboardingComplete value
            const companyRecord = await base('Companies').find(primaryCompanyId);
            const onboardingComplete = companyRecord.fields['OnboardingComplete'];
            
            if (debugLayout) debugLog('[Layout] Checking onboarding status:', {
              companyId: primaryCompanyId,
              onboardingComplete,
              type: typeof onboardingComplete
            });
            
            // Check if onboarding is complete (field is "true" or true)
            const isOnboardingComplete = onboardingComplete === true || onboardingComplete === 'true' || onboardingComplete === 'True';
            
            if (!isOnboardingComplete) {
              // Onboarding not complete - redirect to onboarding-v2
              if (debugLayout) debugLog('[Layout] Personal email user with company but onboarding not complete, redirecting to onboarding-v2');
              navigate(`/onboarding-v2?company=${encodeURIComponent(primaryCompanyId)}`);
              return;
            } else {
              // Onboarding complete - allow access to main app
              if (debugLayout) debugLog('[Layout] Personal email user with company and onboarding complete, allowing access');
            }
          } catch (companyErr) {
            console.error('[Layout] Error checking company onboarding status:', companyErr);
            // On error, redirect to onboarding to be safe
            navigate(`/onboarding-v2?company=${encodeURIComponent(companyIds[0])}`);
            return;
          }
        }
      } catch (error) {
        console.error('[Layout] Error checking personal email redirect:', error);
        // Don't redirect on error - let user continue
      }
    };

    checkPersonalEmailRedirect();
  }, [isClerkLoaded, currentClerkUser, location.pathname, navigate, company]);

  useEffect(() => {
    const website = getQueryVariable('website');
    if (website && companies) {
      const domain = website.split('&')[0]; // Get the domain part before any other parameters
      const foundCompany = companies.find(c =>
        c.fields.Domain === domain ||
        c.fields.Domain === `www.${domain}` ||
        c.fields.Domain === `https://${domain}` ||
        c.fields.Domain === `https://www.${domain}`
      );

      if (foundCompany) {
        // Company found, update the context
        setCompany(foundCompany);
        setDomainStatus('known');
        setLoading(false);
      } else {
        // No company found for this domain
        setDomainStatus('unknown');
        setLoading(false);
      }
    }
  }, [companies, getQueryVariable]);

  // Lightweight badge count for admins — Status field only, not the full requests payload
  useEffect(() => {
    if (!admin) return;

    const fetchSubmittedCount = async () => {
      try {
        const records = await base('automationRequests')
          .select({
            filterByFormula: '{Status} = "Submitted"',
            fields: ['Status'],
            maxRecords: 100,
          })
          .all();
        setNotificationCount(records.length);
      } catch (error) {
        console.error('Error fetching automation request count:', error);
      }
    };

    fetchSubmittedCount();
    const interval = setInterval(fetchSubmittedCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [admin]);

  function submitEmailInvite(email, comment, reportLink) {
    comment = comment + reportLink || 'Hey there lets see how this works!';

    var request = email + ' was just referred for savings ';

    setShowNotif(true, setEmailModalNotif);
    const content = {
      type: 'success',
      heading: `You just referred ${email}!`,
      message:
        "You'll be sent the greater of $500 or 15% of the first 12 months of revenue",
    };
    setContents(content, setEmailModalNotif);

    // https://hooks.zapier.com/hooks/catch/13965335/3ztn14f/
    // fetch(`https://hooks.zapier.com/hooks/catch/13965335/3ztn14f/`, {
    //   method: 'POST',
    //   body: JSON.stringify({ request, email, comment }),
    // })
    //   .then((response) => response.json())
    //   .then((data) => {})
    //   .catch((error) => console.error(error));
  }

  const pushEmailtoAirtableLeads = async (email) => {
    base('leads').create(
      [
        {
          fields: {
            Email: email,
          },
        },
      ],

      function (err, records) {
        if (err) {
          console.error(err);
          return;
        }

        records.forEach(function (record) {
          debugLog(record.getId());
        });
      }
    );
  };

  /**
   * Slack (REACT_APP_SLACK_ONBOARDING_WEBHOOK_URL).
   * Use `{ blocking: true }` before `window.location.href` so navigation does not cancel the POST.
   */
  const sendLeadBarSlackNotification = (partial, { blocking = false } = {}) => {
    const payload = {
      ...partial,
      clerkEmail: currentClerkUser?.primaryEmailAddress?.emailAddress || '',
      clerkName:
        currentClerkUser?.fullName ||
        currentClerkUser?.firstName ||
        currentClerkUser?.username ||
        '',
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    };

    const p = postLeadBarSlackFromBrowser(payload).catch((err) => {
      console.error('[Lead bar] Slack webhook error', err);
    });
    return blocking ? p : undefined;
  };

  const handleSubmit = async (domain) => {
    if (submittingDomain) return; // Prevent multiple submissions

    debugLog('running');
    setSubmittingDomain(true);

    let rawInput = '';
    let capturedLeadEmail = null;

    try {
      rawInput = String(domain || '').trim();
      domain = rawInput;

      // Handle email
      if (domain?.includes('@')) {
        capturedLeadEmail = domain;
        //If an email is detected, first add that to the leads table

        pushEmailtoAirtableLeads(domain);

        domain = domain?.split('@')[1]; // then split the email from the domain
      }

      // Handle website.
      if (
        domain?.startsWith('https://www.') ||
        domain?.startsWith('http://www.') ||
        domain?.startsWith('www.') ||
        domain?.startsWith('http://') ||
        domain?.startsWith('https://')
      ) {
        domain = domain.split('://')[1];

        if (domain?.includes('www.')) {
          domain = domain.split('www.')[1];
        }

        if (domain?.includes('/')) {
          domain = domain.split('/')[0];
        }
      }

      // Checking if the input submitted is a url
      const url = getWebURL(domain);
      if (url !== false) {
        domain = url;
      }

      // Creating the contents object for the notification
      const output = checkValidDomainName(domain);
      let contents = { type: '', heading: '', message: '' };

      debugLog(output.result);

      if (output.result === 'Error') {
        contents.type = 'error';
        contents.heading = `Error in Fetching ${domain}❗`;
        contents.message = output.message;

        sendLeadBarSlackNotification({
          rawInput,
          extractedEmail: capturedLeadEmail,
          normalizedDomain: domain,
          outcome: 'validation_error',
          errorMessage: output.message || undefined,
        });
      }
      // if the domain is valid (i.e. output.result==="Success")
      if (output.result === 'Success') {
        debugLog('DEBUG: Domain is valid, checking for existing company...');

        // Check if company exists by fetching directly from Airtable
        let existingCompany = null;
        try {
          existingCompany = await Promise.race([
            checkCompanyExists(domain),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('company_lookup_timeout')), 15000)
            ),
          ]);
        } catch {
          debugLog('[Lead bar] Company lookup timed out; continuing as new domain');
          existingCompany = null;
        }

        if (existingCompany) {
          // Company exists, redirect to the existing company's domain
          const redirectDomain = existingCompany.fields.Domain;
          const matchedCompanyName = existingCompany.fields.Name;
          debugLog('Redirecting to existing company:', matchedCompanyName, 'at domain:', redirectDomain);

          await Promise.allSettled([
            sendLeadBarSlackNotification(
              {
                rawInput,
                extractedEmail: capturedLeadEmail,
                normalizedDomain: domain,
                outcome: 'existing_company_redirect',
                details: `Matched *${matchedCompanyName || 'Company'}* → \`${redirectDomain}\``,
              },
              { blocking: true }
            ),
            sendLeadBarWelcomeEmail({
              email: capturedLeadEmail,
              domain: redirectDomain,
              companyName: matchedCompanyName,
            }),
          ]);

          window.location.href = `/grants-section?website=${redirectDomain}&company=${matchedCompanyName}`;
        } else {
          // No company row yet. Create it in Airtable and start Clay with the
          // new record id. The browser Clay call never wrote a row, and the
          // callback can only update a company when that id is present.
          const userEmail = currentClerkUser?.primaryEmailAddress?.emailAddress || '';
          let ensureData = {};
          let ensureOk = false;

          try {
            const response = await fetch('/api/ensure-funding-report', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                domain,
                email: capturedLeadEmail || userEmail || undefined,
              }),
            });
            ensureData = await response.json().catch(() => ({}));
            ensureOk = response.ok;
            sessionStorage.setItem(
              'clay_enrich_result',
              JSON.stringify({
                domain,
                data: ensureData,
                ok: response.ok,
                status: response.status,
              })
            );
          } catch (err) {
            console.error('Funding report create error:', err);
            ensureData = { error: err?.message || 'Request failed' };
          }

          if (!ensureOk) {
            contents.type = 'error';
            contents.heading = 'Could not create report';
            contents.message =
              ensureData.error || 'There was an error creating this report. Please try again.';

            sendLeadBarSlackNotification({
              rawInput,
              extractedEmail: capturedLeadEmail,
              normalizedDomain: domain,
              outcome: 'unexpected_error',
              errorMessage: contents.message,
            });
          } else {
            const redirectDomain = ensureData.domain || domain;
            contents.type = 'success';
            contents.heading = 'Your Report is Being Upgraded';
            contents.message = "Your Report will be emailed to you as soon as it's ready!";

            await Promise.allSettled([
              sendLeadBarSlackNotification(
                {
                  rawInput,
                  extractedEmail: capturedLeadEmail,
                  normalizedDomain: redirectDomain,
                  outcome: 'new_enrich_redirect',
                  details: ensureData.created
                    ? `Created Airtable company ${ensureData.companyId || ''}; enrichment ${
                        ensureData.enrichmentStarted ? 'started' : 'not started'
                      }`
                    : `Company already existed (${ensureData.companyId || ''}); redirected to summary`,
                },
                { blocking: true }
              ),
              sendLeadBarWelcomeEmail({
                email: capturedLeadEmail,
                domain: redirectDomain,
              }),
            ]);

            window.location.href = `/grants-section?website=${encodeURIComponent(redirectDomain)}`;
          }
        }
      }

      // If there is a notification to show
      if (contents.type != '') {
        setContents(contents, setSubmitEnrichNotif);
        setShowNotif(true, setSubmitEnrichNotif);
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      // Show error notification
      const errorContents = {
        type: 'error',
        heading: 'Error Processing Request',
        message: 'There was an error processing your request. Please try again.'
      };
      setContents(errorContents, setSubmitEnrichNotif);
      setShowNotif(true, setSubmitEnrichNotif);

      sendLeadBarSlackNotification({
        rawInput,
        extractedEmail: capturedLeadEmail,
        normalizedDomain: typeof domain !== 'undefined' ? domain : undefined,
        outcome: 'unexpected_error',
        errorMessage: error?.message || String(error),
      });
    } finally {
      setSubmittingDomain(false);
    }
  };

  const handleCompanyChange = (e) => {
    const value = e.target.value;
    debugLog('domain changed', value);

    if (value === '') {
      setDomainStatus('');
      return;
    }

    const output = checkValidDomainName(value);

    if (output.result === 'Error') {
      setDomainStatus('invalid');
      return;
    }

    // For real-time validation, we'll use the companies from context if available
    // but fall back to a simple check to avoid too many Airtable calls
    if (companies && companies.length > 0) {
      // Extract base company name from the input
      const baseCompanyName = extractBaseCompanyName(value);
      debugLog('DEBUG: Extracted base company name:', baseCompanyName, 'from input:', value);

      // Check cache first for faster real-time feedback
      if (companyCache.current.has(baseCompanyName)) {
        const cachedResult = companyCache.current.get(baseCompanyName);
        setDomainStatus(cachedResult ? 'known' : 'unknown');
        debugLog('DEBUG: Using cached result for real-time validation:', baseCompanyName);
        return;
      }

      // Check if any existing company has the same base name
      const companiesDomain = companies.filter((company) => {
        if (!company?.fields?.Domain) return false;

        const existingBaseName = extractBaseCompanyName(company.fields.Domain);
        return existingBaseName === baseCompanyName;
      });

      debugLog('DEBUG: Found companies with same base name:', companiesDomain.length);
      companiesDomain.forEach(company => {
        debugLog('DEBUG: Matching company:', company.fields.Name, '(', company.fields.Domain, ')');
      });

      if (companiesDomain.length !== 0) {
        setDomainStatus('known');
        debugLog('DEBUG: Setting domainStatus to "known"');
      } else {
        setDomainStatus('unknown');
        debugLog('DEBUG: Setting domainStatus to "unknown"');
      }
    } else {
      // If companies context is not loaded, set to unknown to allow submission
      // The actual check will happen in handleSubmit
      setDomainStatus('unknown');
    }
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    // Only try to access user email if user exists
    const userEmail = currentClerkUser?.primaryEmailAddress?.emailAddress;
    // Rest of the email handling logic
    setEmailStatus(
      checkEmail(newEmail) ? 'correct' : newEmail === '' ? 'empty' : 'incorrect'
    );
    setDomain(extractDomain(newEmail));
  };

  const [isGrantsOpen, setIsGrantsOpen] = useState(false);
  const toggleGrants = () => {
    setIsGrantsOpen(!isGrantsOpen);
  };

  // Function to save checklist to Airtable
  // Uses 1-based indexing: position 1 = first checkable task (checkedItems[0])
  const isSavingRef = useRef(false);
  
  const saveChecklistToAirtable = async (newCheckedItems) => {
    if (!company?.id) return;
    
    // Prevent double saves
    if (isSavingRef.current) {
      if (debugLayout) debugLog('[OnboardingChecklist] Already saving, skipping duplicate save');
      return;
    }

    isSavingRef.current = true;
    setSavingChecklist(true);
    try {
      // Count checkable items (excluding Complete Profile)
      const checkableCount = newCheckedItems.filter(item => item).length;
      // Include Complete Profile in total count if it's complete
      const profileComplete = isCompanyProfileComplete();
      const trueCount = checkableCount + (profileComplete ? 1 : 0);
      const totalItems = ONBOARDING_TASKS_CONFIG.length;
      
      // Store positions as 1-based: position 1 = checkedItems[0]
      const checkedPositions = newCheckedItems
        .map((checked, index) => checked ? index + 1 : -1)
        .filter(pos => pos !== -1)
        .join(',');
      
      const fractionString = `${trueCount}/${totalItems}${checkedPositions ? `:${checkedPositions}` : ''}`;

      await base('companies').update(company.id, {
        OnboardingChecklist: fractionString
      });

      if (debugLayout) debugLog('[OnboardingChecklist] Saved to Airtable:', fractionString);
    } catch (error) {
      console.error("[OnboardingChecklist] Error saving checklist:", error);
    } finally {
      isSavingRef.current = false;
      setSavingChecklist(false);
    }
  };

  // Onboarding Checklist
  // Note: First task (Complete Profile) is handled separately, so checkedItems only tracks tasks 2-7
  // Position 1 in Airtable = checkedItems[0] (first checkable task after Complete Profile)
  const taskCount = ONBOARDING_TASKS_CONFIG.length - 1; // Exclude first task (Complete Profile)

  const [checkedItems, setCheckedItems] = useState(() => {
    try {
      if (company?.fields?.OnboardingChecklist) {
        const [countPart, positionsPart] = company.fields.OnboardingChecklist.split(':');
        const positions = positionsPart ? positionsPart.split(',').map(Number) : [];
        // Convert 1-based positions to 0-based array: position 1 -> checkedItems[0]
        return Array(taskCount).fill(false).map((_, i) => positions.includes(i + 1));
      }
      return Array(taskCount).fill(false);
    } catch (error) {
      console.error("Error parsing checklist:", error);
      return Array(taskCount).fill(false);
    }
  });

  // Track if Complete Profile is completed based on company fields
  // Complete Profile is checked if all basic info fields (Name through Profitable?) are complete
  const [profileCompleteFromDB, setProfileCompleteFromDB] = useState(false);
  
  // Update profileCompleteFromDB whenever company fields change
  useEffect(() => {
    if (company?.fields) {
      const isComplete = isCompanyProfileComplete();
      setProfileCompleteFromDB(isComplete);
      if (debugLayout) debugLog('[OnboardingChecklist] Profile completion status updated', {
        isComplete,
        companyFields: {
          Name: company.fields.Name,
          Domain: company.fields.Domain,
          Employees: company.fields.Employees,
          Address: company.fields.Address,
          Country: company.fields.Country,
          ProvinceOrState: company.fields.ProvinceOrState,
          Industry: company.fields.Industry,
          Founded: company.fields.Founded,
          'Profitable?': company.fields['Profitable?']
        }
      });
    }
  }, [company?.fields?.Name, company?.fields?.Domain, company?.fields?.Employees, 
      company?.fields?.Address, company?.fields?.Country, company?.fields?.ProvinceOrState,
      company?.fields?.Industry, company?.fields?.Founded, company?.fields?.['Profitable?']]);
  
  // Load initial state from Airtable only once when company data is first available
  const hasLoadedInitialState = useRef(false);
  useEffect(() => {
    // Only load once when company ID is first set
    if (!company?.id || hasLoadedInitialState.current) return;
    if (!company?.fields?.OnboardingChecklist) return;
    
    hasLoadedInitialState.current = true;
    try {
      const [countPart, positionsPart] = company.fields.OnboardingChecklist.split(':');
      const positions = positionsPart ? positionsPart.split(',').map(Number) : [];
      
      // Convert 1-based positions to 0-based array for checkable tasks
      // Database: position 1 = first checkable task = checkedItems[0] (Upload & Redeem Tech Savings)
      // Database: position 2 = second checkable task = checkedItems[1] (Upload Planned NTM Expenses)
      // Database: position 3 = third checkable task = checkedItems[2] (Draft & Apply 5 Grants)
      // etc.
      const initialCheckedItems = Array(taskCount).fill(false).map((_, i) => positions.includes(i + 1));
      
      setCheckedItems(initialCheckedItems);
      if (debugLayout) debugLog('[OnboardingChecklist] Loaded initial state from Airtable', {
        checklist: company.fields.OnboardingChecklist,
        positions,
        initialCheckedItems
      });
    } catch (error) {
      console.error("[OnboardingChecklist] Error loading initial checklist:", error);
    }
  }, [company?.id, taskCount]); // Only run when company ID changes (initial load)

  const activateNavItem = (item) => {
    if (item.onClick) {
      item.onClick({ preventDefault() {} }, navigate, getNavSearch());
      return;
    }
    if (item.isPage || item.href?.startsWith('/')) {
      window.scrollTo({ top: 0 });
      navigate(pathForNavItem(item));
      return;
    }
    const targetId = String(item.href || '').replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    navigate(`/${getNavSearch()}${item.href}`);
  };

  // Keyboard shortcut for command palette (Cmd+K), company profile search (Cmd+Shift+K), feedback (Cmd+Shift+F), and nav letters
  useEffect(() => {
    const navShortcutItems = [...navigationNew, ...adminNavLinks, ...adminQuickLinks].filter((item) => item.shortcut);

    const handleKeyDown = (event) => {
      const meta = event.metaKey || event.ctrlKey;
      // Cmd/Ctrl+Shift+K — company profile search (company → fields / Grant Q&A)
      if (
        meta &&
        event.shiftKey &&
        event.key.toLowerCase() === 'k'
      ) {
        event.preventDefault();
        setCompanyProfileSearchOpen(true);
        return;
      }
      // Cmd/Ctrl+K only — Shift+K is reserved for company profile search
      if (meta && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (admin && !isClosing) {
          debugLog('Layout: Cmd+K pressed, opening command palette');
          setCommandPaletteOpen(true);
        }
        return;
      }
      // Feedback (Cmd/Ctrl + Shift + F)
      if (meta && event.shiftKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        setIsFeedbackModalOpen(true);
        return;
      }

      // Keyboard shortcuts (Cmd/Ctrl + /)
      if (meta && !event.shiftKey && !event.altKey && (event.key === '/' || event.code === 'Slash')) {
        event.preventDefault();
        setShortcutsOpen((open) => !open);
        setCommandPaletteOpen(false);
        setCompanyProfileSearchOpen(false);
        return;
      }

      if (!meta || event.shiftKey || event.altKey || event.repeat) return;
      if (event.key.length !== 1) return;

      // Leave Chrome/Arc (and editing) shortcuts alone: find, find next, print, save,
      // open, bookmark, history, copy/paste/undo/select-all, and window/tab commands.
      const reservedBrowserKeys = new Set([
        'a', 'c', 'v', 'x', 'z',
        'f', 'g', 'e', 'p', 's', 'o', 'd', 'y',
        'l', 'r', 't', 'w', 'n', 'h', 'm', 'q',
      ]);
      if (reservedBrowserKeys.has(event.key.toLowerCase())) return;

      const target = event.target;
      const tag = target?.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
      if (typing) return;
      if (commandPaletteOpen || companyProfileSearchOpen || isFeedbackModalOpen || shortcutsOpen || isAddGrantModalOpen || inviteOpen || opportunityModal.open) return;

      const item = navShortcutItems.find((entry) => entry.shortcut === event.key.toLowerCase());
      if (!item || (item.adminOnly && !admin)) return;

      event.preventDefault();
      activateNavItem(item);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [admin, isClosing, commandPaletteOpen, companyProfileSearchOpen, isFeedbackModalOpen, shortcutsOpen, isAddGrantModalOpen, inviteOpen, opportunityModal.open, navigate, location.search, company]);

  const shortcutSections = useMemo(() => {
    const toKeys = (letter) => ['⌘', String(letter).toUpperCase()];
    const sections = [
      {
        title: 'General',
        items: [
          ...(admin ? [{ name: 'Quick search', keys: ['⌘', 'K'] }] : []),
          { name: 'Company profile search', keys: ['⌘', '⇧', 'K'] },
          { name: 'Support', keys: ['⌘', '⇧', 'F'] },
          { name: 'Keyboard shortcuts', keys: ['⌘', '/'] },
        ],
      },
      {
        title: 'Navigation',
        items: navigationNew
          .filter((item) => item.shortcut && (!item.admin || admin))
          .map((item) => ({ name: item.name, keys: toKeys(item.shortcut) })),
      },
    ];
    if (admin) {
      const teamItems = [...adminNavLinks, ...adminQuickLinks]
        .filter((item) => item.shortcut)
        .map((item) => ({ name: item.name, keys: toKeys(item.shortcut) }));
      if (teamItems.length) sections.push({ title: 'Team', items: teamItems });
    }
    return sections;
  }, [admin]);

  // Allow pages to open company profile search via event (e.g. Profile & Company button)
  useEffect(() => {
    const handleOpen = () => setCompanyProfileSearchOpen(true);
    window.addEventListener(OPEN_COMPANY_PROFILE_SEARCH_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_COMPANY_PROFILE_SEARCH_EVENT, handleOpen);
  }, []);

  const companyProfileSectionNavItems = useMemo(
    () => getCompanySectionNavItems(admin),
    [admin]
  );

  const handleCompanyProfileSearchSelect = useCallback(
    (item, scopedCompany) => {
      if (!item || !scopedCompany?.id) return;

      if (scopedCompany.id !== company?.id) {
        setCompany(scopedCompany);
      }

      const focusTarget = item.focusId || (item.type === 'field' ? item.fieldName : null);
      if (focusTarget) {
        dispatchCompanyProfileFieldFocus(focusTarget);
      }
      if (item.onboardingTab) {
        dispatchCompanyProfileOnboardingFocus({
          tab: item.onboardingTab,
          focusId: item.focusId,
        });
      }

      if (item.type === 'grant' || item.type === 'qa') {
        dispatchCompanyProfileSearchFocus({
          matchingMode: item.matchingMode,
          grantId: item.grantId,
          searchQuery:
            item.type === 'qa' ? item.questionText || item.title || '' : '',
          questionText: item.questionText || '',
        });
      }

      if (item.type === 'call' && item.recordingId) {
        dispatchCompanyProfileCallFocus({
          recordingId: item.recordingId,
          openTranscript: Boolean(item.hasTranscript),
        });
      }

      const url = buildCompanyProfileSearchUrl(scopedCompany, item);
      const onProfilePage = location.pathname.includes('company-info-section');

      if (onProfilePage) {
        navigate(url, { replace: true });
        window.setTimeout(() => {
          if (focusTarget) {
            focusCompanyProfileField(focusTarget);
          }
          if (item.type === 'call' && item.recordingId) {
            focusCompanyProfileCallRecording(item.recordingId, {
              openTranscript: Boolean(item.hasTranscript),
            });
          }
        }, 400);
      } else {
        navigate(url);
      }
    },
    [company?.id, setCompany, navigate, location.pathname]
  );

  // Feedback submission handler
  const handleFeedbackSubmit = async () => {
    if (!feedbackText.trim()) {
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedback: feedbackText,
          pageUrl: window.location.href,
          userEmail: email,
          userName: currentClerkUser?.fullName || currentClerkUser?.firstName || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setFeedbackText('');
      setIsFeedbackModalOpen(false);
      showNotification(setEmailModalNotif, {
        type: 'success',
        heading: 'Feedback Sent',
        message: 'Thank you for your feedback! We appreciate your input.',
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showNotification(setEmailModalNotif, {
        type: 'error',
        heading: 'Error',
        message: 'Failed to submit feedback. Please try again.',
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const allChecked = checkedItems.every(item => item);

  // Debug: Log checkedItems changes
  useEffect(() => {
    if (debugLayout) debugLog('[OnboardingChecklist] checkedItems state changed', {
      checkedItems,
      checkedCount: checkedItems.filter(Boolean).length,
      allChecked,
      isCompanyProfileComplete: isCompanyProfileComplete(),
      totalDisplayCount: checkedItems.filter(Boolean).length + (isCompanyProfileComplete() ? 1 : 0)
    });
  }, [checkedItems, allChecked]);

  // Helper function to update checklist (called on-demand, not via polling)
  const updateChecklistOnDemand = async () => {
    if (!company?.id || !currentClerkUser) return;
    
    const newCheckedItems = [...checkedItems];
    let hasChanges = false;
    
    // Task 1: Complete Profile (handled separately via profileCompleteFromDB)
    
    // Task 2: Upload & Redeem Tech Savings
    try {
      const techSavingsComplete = await isTechSavingsUploaded();
      if (techSavingsComplete !== newCheckedItems[0]) {
        newCheckedItems[0] = techSavingsComplete;
        hasChanges = true;
      }
    } catch (error) {
      console.error('[OnboardingChecklist] Error checking tech savings:', error);
    }
    
    // Task 3: Upload Proforma
    const proformaComplete = isProformaUploaded();
    if (proformaComplete !== newCheckedItems[1]) {
      newCheckedItems[1] = proformaComplete;
      hasChanges = true;
    }
    
    // Task 4: Draft & Apply to 5 Grants
    const grantsComplete = hasFiveGrantsAdded();
    if (grantsComplete !== newCheckedItems[2]) {
      newCheckedItems[2] = grantsComplete;
      hasChanges = true;
    }
    
    // Task 5: Try 3 AI Automations
    const automationsComplete = hasThreeAutomationsHearted();
    if (automationsComplete !== newCheckedItems[3]) {
      newCheckedItems[3] = automationsComplete;
      hasChanges = true;
    }
    
    // Task 6: Try 3 Revenue Upgrades
    try {
      const revenueComplete = await hasThreeRevenueUpgradesHearted();
      if (revenueComplete !== newCheckedItems[4]) {
        newCheckedItems[4] = revenueComplete;
        hasChanges = true;
      }
    } catch (error) {
      console.error('[OnboardingChecklist] Error checking revenue upgrades:', error);
    }
    
    // Task 7: Request 1 Ideal Client Intro
    const introComplete = hasRequestedIntro();
    if (introComplete !== newCheckedItems[5]) {
      newCheckedItems[5] = introComplete;
      hasChanges = true;
    }
    
    // Task 8: Refer & Earn More
    try {
      const referralComplete = await hasAddedReferral();
      if (referralComplete !== newCheckedItems[6]) {
        newCheckedItems[6] = referralComplete;
        hasChanges = true;
      }
    } catch (error) {
      console.error('[OnboardingChecklist] Error checking referrals:', error);
    }
    
    // Only update if something changed
    if (hasChanges) {
      if (debugLayout) debugLog('[OnboardingChecklist] Auto-updating checklist:', newCheckedItems);
      setCheckedItems(newCheckedItems);
      saveChecklistToAirtable(newCheckedItems);
    }
  };

  // Auto-update checklist when relevant company fields change (event-driven)
  useEffect(() => {
    if (!company?.id || !currentClerkUser) return;
    
    // Run check when relevant fields change
    updateChecklistOnDemand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    company?.id,
    company?.fields?.Domain,
    company?.fields?.TopExpenses,
    company?.fields?.InterestedIn,
    company?.fields?.PrioritizedAutomationIds,
    company?.fields?.IntroRequested,
    company?.fields?.PaymentStatus, // For Task 1 (Complete Profile)
    company?.fields?.ValidCreditCard, // For Task 1 (Complete Profile)
    company?.fields?.ManagedClient, // For Task 1 (Complete Profile)
    company?.fields?.TermsAgreement, // For Task 1 (Complete Profile)
    company?.fields?.TermsOfService, // For Task 1 (Complete Profile)
    currentClerkUser?.primaryEmailAddress?.emailAddress,
    location?.pathname // Re-check when navigating to Composio routes
  ]);

  // Light background sync every 5 minutes (as backup, not primary mechanism)
  useEffect(() => {
    if (!company?.id || !currentClerkUser) return;
    
    // Run check every 5 minutes as a backup sync mechanism
    const interval = setInterval(() => {
      updateChecklistOnDemand();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, currentClerkUser?.primaryEmailAddress?.emailAddress]);

  // Add this near the top where other state is defined
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [submittingDomain, setSubmittingDomain] = useState(false);

  const opportunityCurrencyFormat = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const updateOpportunityFormData = (fieldOrData, value) => {
    setOpportunityModal((prev) => {
      if (typeof fieldOrData === 'string' && value !== undefined) {
        return {
          ...prev,
          formData: { ...prev.formData, [fieldOrData]: value },
        };
      }
      if (typeof fieldOrData === 'object' && fieldOrData !== null) {
        return {
          ...prev,
          formData: { ...prev.formData, ...fieldOrData },
        };
      }
      return prev;
    });
  };

  const updateOpportunityFormDataWhenGrantSelected = (grant) => {
    const grantName = grant.fields.Name;
    const grantAmount = grant.fields.AnnualMargin || grant.fields.Amount;

    setOpportunityModal((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        opportunityName: `${grantName} [New]`,
        amount: grantAmount || '',
      },
    }));
  };

  const loadOpportunityModalGrants = async () => {
    if (opportunityModalGrantsLoaded) return;
    try {
      setOpportunityModalGrantsLoaded(true);
      const records = await base('deduped_grants').select({ view: 'Grid view' }).all();
      setOpportunityModalGrants(records);
    } catch (err) {
      console.error('Failed to fetch grants for opportunity modal:', err);
      setOpportunityModalGrantsLoaded(false);
    }
  };

  const loadOpportunityModalCompanies = async () => {
    if (opportunityModalCompaniesLoaded) return;
    try {
      setOpportunityModalCompaniesLoaded(true);
      const records = await base('companies').select({ view: 'Grid view' }).all();
      setOpportunityModalCompanies(records);
    } catch (err) {
      console.error('Failed to fetch companies for opportunity modal:', err);
      setOpportunityModalCompaniesLoaded(false);
    }
  };

  const openAddOpportunityModal = () => {
    const currentCompany = company || null;
    setOpportunityModal({
      open: true,
      mode: 'add',
      opportunity: null,
      selectedGrant: null,
      formData: {
        ...OPPORTUNITY_MODAL_DEFAULT_FORM,
        companyName: currentCompany?.fields?.Name || currentCompany?.fields?.CompanyName || '',
        companyID: currentCompany?.id || '',
      },
    });
    setOpportunityModalSelectedCompany(currentCompany);
    loadOpportunityModalGrants();
    if (admin) loadOpportunityModalCompanies();
  };

  const closeOpportunityModal = () => {
    setOpportunityModal({
      open: false,
      mode: 'add',
      opportunity: null,
      selectedGrant: null,
      formData: { ...OPPORTUNITY_MODAL_DEFAULT_FORM },
    });
    setOpportunityModalSelectedCompany(null);
  };

  const handleOpportunitySelectedGrantChange = (grant) => {
    setOpportunityModal((prev) => ({
      ...prev,
      selectedGrant: grant,
      formData: {
        ...prev.formData,
        opportunityName: grant === 'other' ? '' : prev.formData.opportunityName,
        amount: grant === 'other' ? '' : prev.formData.amount,
      },
    }));
    if (grant && grant !== 'other') {
      updateOpportunityFormDataWhenGrantSelected(grant);
    }
  };

  const handleOpportunitySelectedCompanyChange = (companyRecord) => {
    setOpportunityModalSelectedCompany(companyRecord);
    setOpportunityModal((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        companyName: companyRecord
          ? companyRecord.fields.Name || companyRecord.fields.CompanyName || ''
          : '',
        companyID: companyRecord ? companyRecord.id : '',
      },
    }));
  };

  const handleOpportunityModalSave = async () => {
    const { formData, mode, opportunity, selectedGrant } = opportunityModal;

    if (mode === 'edit') {
      if (!opportunity?.id) {
        console.error('No ID found in opportunity object');
        return;
      }
      try {
        const updateData = {
          Assignee: formData.assignee || 'unassigned',
          CompanyName: formData.companyName,
          CompanyID: formData.companyID,
          OpportunityName: formData.opportunityName,
          UpgradeName: formData.opportunityName,
          Amount: String(formData.amount || 0),
          AdjustedAmount: String(formData.amount || 0),
          Status: formData.status,
          Notes: formData.notes,
          EstCompletionDate: formData.estCompletionDate,
          EstHearBackDate: formData.estHearBackDate,
        };

        if (selectedGrant && selectedGrant !== 'other') {
          updateData.GrantID = selectedGrant.id;
        }

        await base(OPPORTUNITIES_TABLE).update(opportunity.id, updateData);
        closeOpportunityModal();
      } catch (error) {
        console.error('Failed to update opportunity:', error);
      }
      return;
    }

    try {
      const createData = {
        Assignee: formData.assignee || 'unassigned',
        CompanyName: formData.companyName,
        CompanyID: formData.companyID,
        OpportunityName: formData.opportunityName,
        UpgradeName: formData.opportunityName,
        Amount: String(formData.amount || 0),
        AdjustedAmount: String(formData.amount || 0),
        Status: formData.status,
        Notes: formData.notes,
        EstCompletionDate: formData.estCompletionDate,
        EstHearBackDate: formData.estHearBackDate,
      };

      if (selectedGrant && selectedGrant !== 'other') {
        createData.GrantID = selectedGrant.id;
      }

      await base(OPPORTUNITIES_TABLE).create(createData);
      closeOpportunityModal();
    } catch (error) {
      console.error('Error creating opportunity:', error);
    }
  };

  useEffect(() => {
    if (!admin) {
      setIsAddGrantModalOpen(false);
    }
  }, [admin]);

  // Simple cache for company lookups to avoid repeated API calls
  const companyCache = React.useRef(new Map());



  // Function to check if a company exists by base name using Airtable filter
  const checkCompanyExists = async (domain) => {
    const baseCompanyName = extractBaseCompanyName(domain);
    debugLog('DEBUG: Checking for company with base name:', baseCompanyName);

    // Check cache first
    if (companyCache.current.has(baseCompanyName)) {
      const cachedResult = companyCache.current.get(baseCompanyName);
      debugLog('DEBUG: Using cached result for:', baseCompanyName);
      return cachedResult;
    }

    try {
      // Use Airtable's filterByFormula to search for companies with matching base name
      // This searches both the Name field and Domain field for the base company name
      const formula = `OR(
        FIND("${baseCompanyName.toLowerCase()}", LOWER({Name})) > 0,
        FIND("${baseCompanyName.toLowerCase()}", LOWER({Domain})) > 0
      )`;

      const records = await base('companies')
        .select({
          filterByFormula: formula,
          view: 'Grid view',
          maxRecords: 120,
        })
        .all();

      debugLog('DEBUG: Found companies with filter:', records.length);

      // Further filter to ensure exact base name match
      const matchingCompanies = records.filter((company) => {
        if (!company?.fields?.Domain) return false;
        const existingBaseName = extractBaseCompanyName(company.fields.Domain);
        return existingBaseName === baseCompanyName;
      });

      debugLog('DEBUG: Found companies with exact base name match:', matchingCompanies.length);
      matchingCompanies.forEach(company => {
        debugLog('DEBUG: Matching company:', company.fields.Name, '(', company.fields.Domain, ')');
      });

      const result = matchingCompanies.length > 0 ? matchingCompanies[0] : null;

      // Cache the result for 5 minutes
      companyCache.current.set(baseCompanyName, result);
      setTimeout(() => {
        companyCache.current.delete(baseCompanyName);
      }, 5 * 60 * 1000); // 5 minutes

      return result;
    } catch (error) {
      console.error("Error checking company existence:", error);
      return null;
    }
  };

  const handleMobileNavHref = (href, item) => {
    setMobileNavOpen(false);
    window.scrollTo({ top: 0 });
    navigate(pathForNavItem(item || { href, preserveSearch: href !== '/' }, href));
  };

  const teamAdminActive = userNavigation.some(isSubNavItemActive);

  const filteredMobileNavItems = navigationNew.filter((item) => {
    if (['My Reports', 'Onboarding', 'My Upgrades', 'Team Earnings', 'Team Admin'].includes(item.name)) {
      return false;
    }
    if (item.showOnlyWhenCollapsed) return false;
    if (item.name === 'Company Info') return admin || isPartOfWorkspace;
    return !item.admin || (item.admin && admin);
  });

  return (
    <div>
      <DualBootBanner />
      <Progress
        loading={grantLoading}
        JITWorking={JITWorking}
        unknownDomain={unknownDomain}
      />
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex flex-col transition-all duration-200 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}
        onMouseEnter={() => sidebarCollapsed && setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="flex h-screen grow flex-col overflow-hidden border-r border-gray-200 bg-white">
          {/* Brand + collapse */}
          <div className={`relative flex h-16 shrink-0 items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-2 px-4'}`}>
            <img
              className={`cursor-pointer object-contain transition-opacity ${
                sidebarCollapsed
                  ? `h-8 w-8 ${sidebarHovered ? 'opacity-0' : 'opacity-100'}`
                  : 'h-11 w-auto max-w-[200px]'
              }`}
              src={sidebarCollapsed
                ? "/upgraded-collapsed-logo.png"
                : "/upgraded-full-logo.png"}
              alt="Upgraded"
              id="logo"
              onClick={() => { window.location.href = '/'; }}
            />
            {(sidebarCollapsed ? sidebarHovered : true) && (
              <button
                type="button"
                onClick={() => setSidebarCollapsed((prev) => !prev)}
                className={`z-50 p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 ${
                  sidebarCollapsed && sidebarHovered
                    ? 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-100'
                    : 'absolute right-3 top-1/2 -translate-y-1/2 rounded-lg'
                }`}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                  <line x1="9" x2="9" y1="3" y2="21" />
                </svg>
              </button>
            )}
          </div>

          {/* Search — expanded full field, collapsed icon */}
          {admin && (
            <div className={`shrink-0 ${sidebarCollapsed ? 'flex justify-center px-2 pb-2' : 'px-3 pb-3'}`}>
              {sidebarCollapsed ? (
                <Tooltip content="Quick Search (⌘K)" show={hoveredItem === 'search'}>
                  <button
                    type="button"
                    onMouseEnter={() => setHoveredItem('search')}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={() => setCommandPaletteOpen(true)}
                    className={collapsedIconBtnClass(false)}
                    aria-label="Quick search"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </Tooltip>
              ) : (
                <button
                  type="button"
                  onClick={() => setCommandPaletteOpen(true)}
                  className="flex w-full items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-left text-sm text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-50"
                >
                  <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">Search</span>
                  <span className="inline-flex shrink-0 items-center rounded-md border border-gray-200 bg-white px-1.5 py-0.5 font-sans text-[11px] leading-none text-gray-400">
                    ⌘K
                  </span>
                </button>
              )}
            </div>
          )}

          <nav className="flex min-h-0 flex-1 flex-col">
            <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto ${sidebarCollapsed ? 'px-2' : ''}`}>
              {sidebarSubNav && admin && !sidebarCollapsed ? (
                  <div>
                    <button
                      type="button"
                      onClick={closeSidebarSubNav}
                      className="relative mb-1 flex w-full items-center px-3 py-1.5 text-xs font-normal text-gray-900 transition-colors hover:bg-gray-50"
                      aria-label="Back to navigation"
                    >
                      <ChevronLeftIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                      <span className="flex-1 text-center">{SIDEBAR_SUB_NAVS[sidebarSubNav].title}</span>
                      <span className="h-4 w-4 shrink-0" aria-hidden="true" />
                    </button>
                    {renderSidebarSubNavSections(SIDEBAR_SUB_NAVS[sidebarSubNav].sections, {
                      isSubNavItemActive,
                      handleSubNavClick,
                    })}
                  </div>
                ) : (
                <ul role="list" className={`space-y-0.5 ${sidebarCollapsed ? 'flex flex-col items-center gap-y-1' : ''}`}>
                  {navigationNew
                    .filter((item) => {
                      if (item.secondary) return false;
                      // Skip items that are excluded by name
                      if (['My Reports', 'Onboarding', 'My Upgrades', 'Team Earnings', 'Team Admin'].includes(item.name)) {
                        return false;
                      }
                      // Items that only show when collapsed should be filtered based on sidebar state
                      if (item.showOnlyWhenCollapsed && !sidebarCollapsed) {
                        return false;
                      }
                      if (sidebarCollapsed && item.hideWhenCollapsed) {
                        return false;
                      }
                      // Company Info: only show if admin OR part of workspace
                      if (item.name === 'Company Info') {
                        return admin || isPartOfWorkspace;
                      }
                      // Show item if it doesn't have admin flag, OR if it has admin flag and user is admin
                      return !item.admin || (item.admin && admin);
                    })
                    .map((item) => (
                      <li key={item.name}>
                        {item.expandable ? (
                          // Expandable item (like Grants or AI Automations)
                          <div>
                            {sidebarCollapsed ? (
                              // When sidebar is collapsed, show tooltip and navigate directly
                              <Tooltip
                                content={item.name}
                                show={hoveredItem === item.name}
                              >
                                <button
                                  onMouseEnter={() => setHoveredItem(item.name)}
                                  onMouseLeave={() => setHoveredItem(null)}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    // For expandable items, navigate to their href if it's a page route
                                    if (item.isPage && item.href.startsWith('/')) {
                                      window.scrollTo({ top: 0 });
                                      navigate(pathForNavItem(item));
                                    } else if (item.href.startsWith('/')) {
                                      // It's a page route
                                      window.scrollTo({ top: 0 });
                                      navigate(pathForNavItem(item));
                                    } else {
                                      const targetId = item.href.replace('#', '');
                                      const element = document.getElementById(targetId);
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth' });
                                      }
                                      navigate(`/${getNavSearch()}${item.href}`);
                                    }
                                  }}
                                  className={collapsedIconBtnClass(
                                    isActive(item.href) ||
                                      (item.subItems &&
                                        item.subItems.some((subItem) => isActive(subItem.href)))
                                  )}
                                >
                                  <item.icon
                                    className={`h-5 w-5 shrink-0 ${
                                      isActive(item.href) || (item.subItems && item.subItems.some(subItem => isActive(subItem.href)))
                                        ? 'text-gray-900'
                                        : 'text-gray-400 group-hover:text-gray-900'
                                    }`}
                                    aria-hidden="true"
                                  />
                                </button>
                              </Tooltip>
                            ) : (
                              // When sidebar is expanded, show normal expandable behavior
                              <button
                                onClick={(e) => {
                                  // Toggle expand/collapse
                                  if (item.name === 'Grants') {
                                    toggleGrants();
                                  }
                                  // If it's a page route, also navigate to it with query params
                                  if (item.isPage && item.href.startsWith('/')) {
                                    navigate(pathForNavItem(item));
                                  }
                                }}
                                className={`group flex w-full items-center justify-between px-3 py-2 text-sm font-medium transition-colors ${
                                  isActive(item.href) || (item.subItems && item.subItems.some(subItem => isActive(subItem.href)))
                                    ? 'bg-gray-100 text-gray-900'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                              >
                                <div className="flex items-center gap-x-3">
                                  <item.icon
                                    className={`h-5 w-5 shrink-0 ${
                                      isActive(item.href) || (item.subItems && item.subItems.some(subItem => isActive(subItem.href)))
                                        ? 'text-gray-900'
                                        : 'text-gray-400 group-hover:text-gray-900'
                                    }`}
                                    aria-hidden="true"
                                  />
                                  <span>{item.name}</span>
                                </div>
                                {/* {item.pro === true && !sidebarCollapsed ? (
                                  <span className="inline-flex items-center rounded-md bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                                    Pro
                                  </span>
                                ) : null} */}
                                <ChevronDownIcon
                                  className={`h-4 w-4 transition-transform duration-200 ${
                                    isActive(item.href) || (item.subItems && item.subItems.some(subItem => isActive(subItem.href)))
                                      ? 'text-gray-900'
                                      : 'text-gray-400'
                                  } ${
                                    (item.name === 'Grants' && isGrantsOpen)
                                      ? 'rotate-180' : ''
                                  }`}
                                />
                              </button>
                            )}
                            {(item.name === 'Grants' && isGrantsOpen) && !sidebarCollapsed && (
                              <ul className="mt-0.5 space-y-0.5">
                                {item.subItems.map((subItem) => (
                                  <li key={subItem.name}>
                                    <a
                                      href={subItem.href}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (subItem.isPage) {
                                          window.scrollTo({ top: 0 });
                                          navigate(pathForNavItem(subItem));
                                          return;
                                        }
                                        const targetId = subItem.href.replace('#', '');
                                        const element = document.getElementById(targetId);
                                        if (element) {
                                          element.scrollIntoView({ behavior: 'smooth' });
                                        }
                                        navigate(`/${getNavSearch()}${subItem.href}`);
                                      }}
                                      className={`group flex gap-x-3 py-2 pl-11 pr-3 text-sm font-medium transition-colors ${
                                        isActive(subItem.href)
                                          ? 'bg-gray-100 text-gray-900'
                                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                      }`}
                                    >
                                      <subItem.icon
                                        className={`h-5 w-5 shrink-0 ${
                                          isActive(subItem.href)
                                            ? 'text-gray-900'
                                            : 'text-gray-400 group-hover:text-gray-900'
                                        }`}
                                        aria-hidden="true"
                                      />
                                      {subItem.name}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ) : (
                          // Regular item
                          <Tooltip
                            content={navTooltip(item)}
                            show={sidebarCollapsed && hoveredItem === item.name}
                            className={sidebarCollapsed ? 'inline-flex' : 'block w-full'}
                          >
                            <a
                              href={item.href}
                              title={sidebarCollapsed ? navTooltip(item) : undefined}
                              onMouseEnter={() => sidebarCollapsed && setHoveredItem(item.name)}
                              onMouseLeave={() => setHoveredItem(null)}
                              onClick={(e) => {
                                e.preventDefault();
                                if (item.isPage) {
                                  window.scrollTo({ top: 0 });
                                  navigate(pathForNavItem(item));
                                  return;
                                }
                                const targetId = item.href.replace('#', '');
                                const element = document.getElementById(targetId);
                                if (element) {
                                  element.scrollIntoView({ behavior: 'smooth' });
                                }
                                navigate(`/${getNavSearch()}${item.href}`);
                              }}
                              className={
                                sidebarCollapsed
                                  ? collapsedIconBtnClass(isActive(item.href))
                                  : `group flex w-full items-center gap-x-3 px-3 py-2 text-sm font-medium transition-colors ${
                                      isActive(item.href)
                                        ? 'bg-gray-100 text-gray-900'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`
                              }
                            >
                              <item.icon
                                className={`h-5 w-5 shrink-0 ${
                                  isActive(item.href)
                                    ? 'text-gray-900'
                                    : 'text-gray-400 group-hover:text-gray-900'
                                }`}
                                aria-hidden="true"
                              />
                              <span className={sidebarCollapsed ? 'hidden' : 'min-w-0 flex-1 truncate'}>{item.name}</span>
                              {!sidebarCollapsed && <NavShortcutHint letter={item.shortcut} />}
                              {/* {item.pro === true && !sidebarCollapsed ? (
                                <span className="inline-flex items-center rounded-md bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                                  Pro
                                </span>
                              ) : null} */}
                            </a>
                          </Tooltip>
                        )}
                      </li>
                    ))}
                  {admin && sidebarCollapsed && (
                    <>
                      {[...adminNavLinks, ...adminQuickLinks].map((item) => (
                        <li key={item.name}>
                          <Tooltip
                            content={navTooltip(item)}
                            show={hoveredItem === item.name}
                          >
                            <button
                              type="button"
                              onMouseEnter={() => setHoveredItem(item.name)}
                              onMouseLeave={() => setHoveredItem(null)}
                              onClick={() => {
                                window.scrollTo({ top: 0 });
                                navigate(`${item.href}${getNavSearch()}`);
                              }}
                              className={collapsedIconBtnClass(isSubNavItemActive(item))}
                            >
                              <item.icon
                                className={`h-5 w-5 shrink-0 ${
                                  isSubNavItemActive(item)
                                    ? 'text-gray-900'
                                    : 'text-gray-400 group-hover:text-gray-900'
                                }`}
                                aria-hidden="true"
                              />
                            </button>
                          </Tooltip>
                        </li>
                      ))}
                      <li>
                        <Tooltip
                          content="Team Admin"
                          show={hoveredItem === 'Team Admin'}
                        >
                          <button
                            type="button"
                            aria-label="Team Admin"
                            onMouseEnter={() => setHoveredItem('Team Admin')}
                            onMouseLeave={() => setHoveredItem(null)}
                            onClick={() => openSidebarSubNav('admin')}
                            className={collapsedIconBtnClass(teamAdminActive)}
                          >
                            <ShieldCheckIcon
                              className={`h-5 w-5 shrink-0 ${
                                teamAdminActive
                                  ? 'text-gray-900'
                                  : 'text-gray-400 group-hover:text-gray-900'
                              }`}
                              aria-hidden="true"
                            />
                          </button>
                        </Tooltip>
                      </li>
                    </>
                  )}
                  {admin && !sidebarCollapsed && (
                    <>
                      <div className="my-2 border-t border-gray-100" />
                      {adminNavLinks.map((item) => (
                        <li key={item.name}>
                          <button
                            type="button"
                            onClick={() => {
                              window.scrollTo({ top: 0 });
                              navigate(`${item.href}${getNavSearch()}`);
                            }}
                            className={`group flex w-full items-center gap-x-3 px-3 py-2 text-left text-sm font-medium transition-colors ${
                              isSubNavItemActive(item)
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon
                              className={`h-5 w-5 shrink-0 ${
                                isSubNavItemActive(item)
                                  ? 'text-gray-900'
                                  : 'text-gray-400 group-hover:text-gray-900'
                              }`}
                              aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1 truncate">{item.name}</span>
                            <NavShortcutHint letter={item.shortcut} />
                          </button>
                        </li>
                      ))}
                      {adminQuickLinks.map((item) => (
                        <li key={item.name}>
                          <button
                            type="button"
                            onClick={() => {
                              window.scrollTo({ top: 0 });
                              navigate(`${item.href}${getNavSearch()}`);
                            }}
                            className={`group flex w-full items-center gap-x-3 px-3 py-2 text-left text-sm font-medium transition-colors ${
                              isSubNavItemActive(item)
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon
                              className={`h-5 w-5 shrink-0 ${
                                isSubNavItemActive(item)
                                  ? 'text-gray-900'
                                  : 'text-gray-400 group-hover:text-gray-900'
                              }`}
                              aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1 truncate">{item.name}</span>
                            <NavShortcutHint letter={item.shortcut} />
                          </button>
                        </li>
                      ))}
                      <li>
                        <button
                          type="button"
                          onClick={() => openSidebarSubNav('admin')}
                          className="group flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                        >
                          <div className="flex items-center gap-x-3">
                            <ShieldCheckIcon
                              className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-gray-900"
                              aria-hidden="true"
                            />
                            <span>Team Admin</span>
                          </div>
                          <ChevronRightIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                        </button>
                      </li>
                    </>
                  )}
                </ul>
                )}
            </div>

            {/* Secondary utilities + profile footer */}
            {!sidebarSubNav && (
              <div className={`relative z-10 shrink-0 overflow-visible ${sidebarCollapsed ? 'px-2 py-2' : 'py-3'}`}>
                {sidebarCollapsed ? (
                  <ul role="list" className="flex flex-col items-center gap-y-1">
                    {admin && (
                      <li className="flex w-full justify-center">
                        <Tooltip content="Add to Grant Index" show={hoveredItem === 'add-grant'}>
                          <button
                            type="button"
                            onMouseEnter={() => setHoveredItem('add-grant')}
                            onMouseLeave={() => setHoveredItem(null)}
                            onClick={() => setIsAddGrantModalOpen(true)}
                            className={collapsedIconBtnClass(false)}
                          >
                            <CircleStackIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                          </button>
                        </Tooltip>
                      </li>
                    )}
                    <li className="flex w-full justify-center">
                      <Tooltip content="Add Opportunity" show={hoveredItem === 'add-opportunity'}>
                        <button
                          type="button"
                          onMouseEnter={() => setHoveredItem('add-opportunity')}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={openAddOpportunityModal}
                          className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white hover:bg-gray-800 transition-colors duration-150"
                        >
                          <PlusIcon className="h-5 w-5 shrink-0 text-white" aria-hidden="true" />
                        </button>
                      </Tooltip>
                    </li>
                    <li className="flex w-full justify-center">
                      <Tooltip content="Support" show={hoveredItem === 'support'}>
                        <button
                          type="button"
                          onMouseEnter={() => setHoveredItem('support')}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={() => setIsFeedbackModalOpen(true)}
                          className={collapsedIconBtnClass(false)}
                        >
                          <QuestionMarkCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        </button>
                      </Tooltip>
                    </li>
                    <li className="flex w-full justify-center">
                      <Tooltip content="Profile & Company" show={hoveredItem === 'settings'}>
                        <button
                          type="button"
                          onMouseEnter={() => setHoveredItem('settings')}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={() => {
                            window.scrollTo({ top: 0 });
                            navigate(`/company-info-section${getNavSearch()}`);
                          }}
                          className={collapsedIconBtnClass(isActive('/company-info-section'))}
                        >
                          <IdentificationIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        </button>
                      </Tooltip>
                    </li>
                  </ul>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={() => setSidebarUtilsOpen((prev) => !prev)}
                      className="group flex w-full items-center justify-between bg-gray-50 px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                      aria-expanded={sidebarUtilsOpen}
                    >
                      <span className="flex items-center gap-x-3">
                        <EllipsisHorizontalIcon className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-gray-900" aria-hidden="true" />
                        <span>More</span>
                        {admin && notificationCount > 0 && !sidebarUtilsOpen && (
                          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-900 px-1.5 text-[10px] font-bold leading-none text-white">
                            {notificationCount}
                          </span>
                        )}
                      </span>
                      <ChevronDownIcon
                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${sidebarUtilsOpen ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </button>

                    {sidebarUtilsOpen && (
                      <ul role="list" className="mt-0.5 space-y-0.5">
                        <li>
                          <button
                            type="button"
                            onClick={() =>
                              window.open(
                                'https://www.getupgraded.ca/free-savings-consultation',
                                '_blank'
                              )
                            }
                            className="group flex w-full items-center gap-x-3 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                          >
                            <PhoneIcon className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-gray-900" aria-hidden="true" />
                            <span>Earnings Strategy Call</span>
                          </button>
                        </li>

                        {admin && (
                          <>
                            <li>
                              <button
                                type="button"
                                onClick={() => {
                                  const currentSearch = getNavSearch();
                                  window.scrollTo({ top: 0 });
                                  navigate(`/automation-requests${currentSearch}`);
                                  setNotificationCount(0);
                                }}
                                className="group relative flex w-full items-center gap-x-3 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                              >
                                <BellIcon className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-gray-900" aria-hidden="true" />
                                <span className="flex-1 text-left">Notifications</span>
                                {notificationCount > 0 && (
                                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-900 px-1.5 text-[10px] font-bold leading-none text-white">
                                    {notificationCount}
                                  </span>
                                )}
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                onClick={() => setIsAddGrantModalOpen(true)}
                                className="group flex w-full items-center gap-x-3 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                              >
                                <CircleStackIcon className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-gray-900" aria-hidden="true" />
                                <span>Add to Grant Index</span>
                              </button>
                            </li>
                          </>
                        )}
                        <li>
                          <button
                            type="button"
                            onClick={openAddOpportunityModal}
                            className="group flex w-full items-center gap-x-3 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
                          >
                            <PlusIcon className="h-5 w-5 shrink-0 text-white" aria-hidden="true" />
                            <span>Add Opportunity</span>
                          </button>
                        </li>

                        <li>
                          <button
                            type="button"
                            onClick={() => setIsFeedbackModalOpen(true)}
                            className="group flex w-full items-center gap-x-3 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                          >
                            <QuestionMarkCircleIcon className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-gray-900" aria-hidden="true" />
                            <span>Support</span>
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            onClick={() => setShortcutsOpen(true)}
                            className="group flex w-full items-center gap-x-3 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                          >
                            <span className="min-w-0 flex-1 text-left">Keyboard shortcuts</span>
                            <NavShortcutHint letter="/" />
                          </button>
                        </li>
                      </ul>
                    )}

                    {/* Enrich — always visible below More */}
                    <div className="mt-3 space-y-3 px-3">
                      <form
                        onSubmit={async (event) => {
                          event.preventDefault();
                          const named = event.currentTarget.elements.namedItem(
                            'enrichCompany'
                          );
                          const domain =
                            named && typeof named.value === 'string' ? named.value : '';
                          await handleSubmit(domain);
                        }}
                      >
                        <div
                          className={`rounded-lg bg-white px-3 pt-2.5 pb-1.5 outline outline-1 -outline-offset-1 focus-within:outline-2 focus-within:-outline-offset-2 ${
                            domainStatus !== 'invalid'
                              ? 'outline-gray-200 focus-within:outline-gray-900'
                              : 'outline-red-600 focus-within:outline-red-600'
                          }`}
                        >
                          <label
                            htmlFor="enrichCompanyInput"
                            className="block text-xs font-medium text-gray-900"
                          >
                            New Improve Earnings Report
                          </label>
                          <div className="flex items-center gap-1">
                            <input
                              type="website"
                              name="enrichCompany"
                              id="enrichCompanyInput"
                              className="block w-full text-sm/6 text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:opacity-50"
                              placeholder="companywebsite.com"
                              onKeyDown={async (event) => {
                                if (
                                  event.key === 'Enter' &&
                                  submitEnrichNotif.showNotif === false
                                ) {
                                  event.preventDefault();
                                  const domain = event.target.value;
                                  await handleSubmit(domain);
                                }
                              }}
                              onChange={handleCompanyChange}
                              disabled={loading || submittingDomain}
                            />
                            <button
                              type="submit"
                              disabled={submittingDomain}
                              className="shrink-0 rounded-md p-1 text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                              aria-label="Submit company domain"
                            >
                              {submittingDomain ? (
                                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                              ) : (
                                <BoltIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                  fill="black"
                                />
                              )}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>

                    <div className="mt-3 px-3">
                      <button
                        type="button"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
                        onClick={() => {
                          const currentSearch = getNavSearch();
                          window.scrollTo({ top: 0 });
                          navigate(`/pricing${currentSearch}`);
                        }}
                      >
                        Upgrade, Earn More
                      </button>
                    </div>
                  </div>
                )}

                <div className={`${sidebarCollapsed ? 'mt-2' : 'mt-3 px-3'}`}>
                  <Menu as="div" className="relative">
                    <MenuButton
                      className={
                        sidebarCollapsed
                          ? 'mx-auto flex flex-col items-center gap-2 rounded-xl p-1 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2'
                          : 'flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left transition-colors hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2'
                      }
                    >
                      {/* Collapsed: company logo above avatar */}
                      {sidebarCollapsed && (company?.fields?.LogoUrl || getQueryVariable('website')) && (
                        <img
                          className="h-8 w-8 shrink-0 rounded-md object-cover ring-1 ring-gray-100"
                          src={
                            company?.fields?.LogoUrl
                              ? company.fields.LogoUrl
                              : `https://logo.clearbit.com/${getQueryVariable('website')}`
                          }
                          alt={company?.fields?.Name || 'Company'}
                        />
                      )}

                      {/* User avatar */}
                      {currentClerkUser?.imageUrl ? (
                        <img
                          className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-gray-100"
                          src={currentClerkUser.imageUrl}
                          alt={currentClerkUser.fullName || currentClerkUser.firstName || 'Account'}
                        />
                      ) : (
                        <span className="inline-block shrink-0 rounded-full">
                          <Skeleton circle height={40} width={40} />
                        </span>
                      )}

                      {!sidebarCollapsed && (
                        <>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-gray-900">
                              {currentClerkUser?.fullName ||
                                currentClerkUser?.firstName ||
                                company?.fields?.Name ||
                                'Account'}
                            </p>
                            <p className="truncate text-sm text-gray-500">
                              {currentClerkUser?.primaryEmailAddress?.emailAddress ||
                                company?.fields?.Domain ||
                                ''}
                            </p>
                          </div>
                          {(company?.fields?.LogoUrl || getQueryVariable('website')) ? (
                            <img
                              className="h-8 w-8 shrink-0 rounded-md object-cover ring-1 ring-gray-100"
                              src={
                                company?.fields?.LogoUrl
                                  ? company.fields.LogoUrl
                                  : `https://logo.clearbit.com/${getQueryVariable('website')}`
                              }
                              alt={company?.fields?.Name || 'Company'}
                            />
                          ) : (
                            <ChevronUpDownIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
                          )}
                        </>
                      )}
                    </MenuButton>

                    <MenuItems
                      portal
                      anchor={
                        sidebarCollapsed
                          ? { to: 'right end', gap: 8, padding: 8 }
                          : { to: 'top start', gap: 8, padding: 8 }
                      }
                      className="z-[60] w-64 origin-bottom-left rounded-lg bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
                    >
                        <MenuItem>
                          {({ focus }) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                const website = getQueryVariable('website');
                                if (!website) return;
                                const domain = website.split('&')[0];
                                window.scrollTo({ top: 0 });
                                navigate(`/company-info/${domain}${getNavSearch()}`);
                                setTimeout(() => {
                                  window.location.reload();
                                }, 100);
                              }}
                              className={classNames(
                                focus ? 'bg-gray-50' : '',
                                'block px-3 py-2 text-sm leading-6 text-gray-600'
                              )}
                            >
                              <BuildingOffice2Icon
                                className="mr-2 inline-block h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                              <span className="relative top-0.5 inline-block">All Company Info</span>
                            </a>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ focus }) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                window.scrollTo({ top: 0 });
                                navigate(`/company-info-section${getNavSearch()}`);
                              }}
                              className={classNames(
                                focus ? 'bg-gray-50' : '',
                                'block px-3 py-2 text-sm leading-6 text-gray-600'
                              )}
                            >
                              <Cog6ToothIcon
                                className="mr-2 inline-block h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                              Profile & Company
                            </a>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ focus }) => (
                            <a
                              href="https://app.asana.com/1/1204481977610635/project/1211334447615100/board"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={classNames(
                                focus ? 'bg-gray-50' : '',
                                'block px-3 py-2 text-sm leading-6 text-gray-600'
                              )}
                            >
                              <LightBulbIcon
                                className="mr-2 inline-block h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                              Feedback
                            </a>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              type="button"
                              onClick={async () => {
                                await signOut();
                                window.location.href = '/sign-up';
                              }}
                              className={classNames(
                                focus ? 'bg-gray-50' : '',
                                'block w-full px-3 py-2 text-left text-sm leading-6 text-gray-600'
                              )}
                            >
                              <ArrowRightStartOnRectangleIcon
                                className="mr-2 inline-block h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                              Sign out
                            </button>
                          )}
                        </MenuItem>
                    </MenuItems>
                  </Menu>
                </div>
              </div>
            )}
          </nav>
        </div>
      </div>
      <div className={sidebarCollapsed ? 'lg:pl-16 overflow-visible' : 'lg:pl-64 overflow-visible'}>
        {/* Mobile-only top bar — desktop chrome lives in the sidebar */}
        <header className="sticky top-0 z-30 flex h-14 min-w-0 max-w-full shrink-0 items-center gap-2 border-b border-gray-100 bg-white px-3 lg:hidden">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="-m-2.5 shrink-0 p-2.5 text-gray-600"
          >
            <span className="sr-only">Open navigation menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex min-w-0 flex-1 justify-center px-1">
            {!company?.fields?.LogoUrl && !getQueryVariable('website') ? (
              <span className="inline-block rounded-md"><Skeleton circle height={36} width={36} /></span>
            ) : (
              <img
                className="h-8 w-auto max-w-[min(160px,calc(100vw-116px))] rounded-md object-contain"
                src={
                  company?.fields?.LogoUrl
                    ? company.fields.LogoUrl
                    : `https://logo.clearbit.com/${getQueryVariable('website')}`
                }
                alt=""
              />
            )}
          </div>

          <div className="shrink-0 py-1">
            <UserButton afterSignOutUrl="/sign-in" />
          </div>
        </header>

        {location.pathname === '/software-savings' && vendorSavingsActions?.openAddExpenseModal && (
          <div className="hidden items-center gap-2 border-b border-gray-100 bg-white px-6 py-3 lg:flex">
            <h1 className="mr-auto text-xl font-semibold tracking-tight text-gray-900">Software Savings</h1>
            <button
              type="button"
              onClick={vendorSavingsActions.openAddExpenseModal}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-gray-800"
            >
              <PlusIcon className="h-4 w-4" />
              Add Expense
            </button>
            <label className="inline-flex cursor-pointer items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50">
              {vendorSavingsActions.csvUploadLoading ? 'Uploading...' : 'Upload CSV'}
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={vendorSavingsActions.handleCsvUpload}
                disabled={vendorSavingsActions.csvUploadLoading}
              />
            </label>
            {vendorSavingsActions.csvUploadFeedback?.show && (
              <div className={`whitespace-pre-line text-sm ${
                vendorSavingsActions.csvUploadFeedback.type === 'error' ? 'text-red-600' :
                vendorSavingsActions.csvUploadFeedback.type === 'warning' ? 'text-amber-600' :
                'text-emerald-600'
              }`}>
                {vendorSavingsActions.csvUploadFeedback.message}
              </div>
            )}
          </div>
        )}

        {/* Mobile navigation drawer */}
        <Transition.Root show={mobileNavOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileNavOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/60" aria-hidden="true" />
            </Transition.Child>
            <div className="fixed inset-0 z-50 flex lg:hidden">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-auto flex max-h-full w-[min(100vw,20rem)] flex-col bg-white shadow-xl">
                  <div className="flex h-14 items-center justify-between border-b border-gray-200 px-4">
                    {sidebarSubNav && admin ? (
                      <>
                        <button
                          type="button"
                          className="-mx-2 flex flex-1 items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs font-normal text-gray-900 hover:bg-gray-50"
                          onClick={closeSidebarSubNav}
                        >
                          <ChevronLeftIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                          {SIDEBAR_SUB_NAVS[sidebarSubNav].title}
                        </button>
                        <button
                          type="button"
                          className="-m-2 rounded-md p-2 text-gray-500 hover:bg-gray-100"
                          onClick={() => setMobileNavOpen(false)}
                        >
                          <span className="sr-only">Close menu</span>
                          <XMarkIcon className="h-5 w-5" aria-hidden />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-semibold text-gray-900">Navigate</span>
                        <button
                          type="button"
                          className="-m-2 rounded-md p-2 text-gray-500 hover:bg-gray-100"
                          onClick={() => setMobileNavOpen(false)}
                        >
                          <span className="sr-only">Close menu</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden />
                        </button>
                      </>
                    )}
                  </div>
                  <nav className="flex-1 overflow-y-auto px-2 py-2">
                    {sidebarSubNav && admin ? (
                      renderSidebarSubNavSections(SIDEBAR_SUB_NAVS[sidebarSubNav].sections, {
                        isSubNavItemActive,
                        handleSubNavClick,
                        onAfterClick: () => setMobileNavOpen(false),
                        compact: true,
                      })
                    ) : (
                    <ul className="space-y-1">
                      {filteredMobileNavItems.map((item) => (
                        <li key={item.name}>
                          {item.subItems && item.subItems.length > 0 ? (
                            <div className="pt-1">
                              <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                {item.name}
                              </p>
                              <ul className="space-y-0.5">
                                {item.subItems.map((sub) => (
                                  <li key={sub.name}>
                                    <button
                                      type="button"
                                      onClick={() => handleMobileNavHref(sub.href, sub)}
                                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                      <sub.icon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
                                      {sub.name}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleMobileNavHref(item.href, item)}
                              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              <item.icon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
                              {item.name}
                            </button>
                          )}
                        </li>
                      ))}
                      {admin && (
                        <>
                          <li aria-hidden="true">
                            <div className="my-2 border-t border-gray-100" />
                          </li>
                          {adminNavLinks.map((item) => (
                            <li key={item.name}>
                              <button
                                type="button"
                                onClick={() => handleMobileNavHref(item.href, item)}
                                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                                  isSubNavItemActive(item)
                                    ? 'bg-gray-100 text-gray-900'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                              >
                                <item.icon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
                                {item.name}
                              </button>
                            </li>
                          ))}
                          {adminQuickLinks.map((item) => (
                            <li key={item.name}>
                              <button
                                type="button"
                                onClick={() => handleMobileNavHref(item.href, item)}
                                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                                  isSubNavItemActive(item)
                                    ? 'bg-gray-100 text-gray-900'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                              >
                                <item.icon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
                                {item.name}
                              </button>
                            </li>
                          ))}
                          <li>
                            <button
                              type="button"
                              onClick={() => openSidebarSubNav('admin')}
                              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            >
                              <span className="flex items-center gap-3">
                                <ShieldCheckIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
                                Team Admin
                              </span>
                              <ChevronRightIcon className="h-4 w-4 text-gray-400" aria-hidden />
                            </button>
                          </li>
                        </>
                      )}
                    </ul>
                    )}
                  </nav>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
        <div className="lg:hidden sticky top-14 mt-2 z-20 h-14 shrink-0 items-center gap-x-4 bg-white border-b px-4 pb-1 pt-2 sm:gap-x-6 sm:px-6 lg:px-8">
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              const named =
                event.currentTarget.elements.namedItem('enrichCompany');
              const domain =
                named && typeof named.value === 'string' ? named.value : '';
              await handleSubmit(domain);
            }}
            className="mt-auto relative flex flex-grow items-stretch max-h-[40px]"
          >
            <input
              type="website"
              name="enrichCompany"
              id="enrichCompanyInputMobile"
              className={`text-sm block w-full rounded-none rounded-l-md border-0 py-1.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 outline-none focus:outline-none focus:ring-2 focus:ring-inset ${domainStatus != 'invalid'
                ? 'focus:ring-indigo-600'
                : 'focus:ring-red-600'
                }`}
              placeholder="moreearningsforfriend@company.com"
              onKeyDown={async (event) => {
                if (
                  event.key === 'Enter' &&
                  submitEnrichNotif.showNotif === false
                ) {
                  event.preventDefault();
                  const domain = event.target.value;
                  await handleSubmit(domain);
                }
              }}
              onChange={handleCompanyChange}
              disabled={loading || submittingDomain}
            />

            <button
              type="submit"
              disabled={submittingDomain || submitEnrichNotif.showNotif}
              className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submittingDomain ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
              ) : (
                <BoltIcon className="h-5 w-5" aria-hidden="true" fill="black" />
              )}
            </button>
          </form>
        </div>
        <main>
          {/* Keyed per route so a pending chunk mounts a fresh boundary. Without the key,
              React Router's startTransition keeps the previous page on screen with no
              feedback, which reads as an unresponsive sidebar. */}
          <Suspense key={location.pathname} fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
        <KeyMetricsNav sidebarCollapsed={sidebarCollapsed} getNavSearch={getNavSearch} />
        <OnboardingChecklistWidget
          company={company}
          checkedItems={checkedItems}
          profileCompleteFromDB={profileCompleteFromDB}
          sidebarCollapsed={sidebarCollapsed}
        />
      </div>
      <Transition.Root show={inviteOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setInviteOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      const email = event.target['email'].value;
                      const comment = event.target['comment'].value;
                      const reportLink = event.target['reportLink'].value;
                      // debugLog(comment);
                      submitEmailInvite(email, comment, reportLink);
                    }}
                    className="space-y-6"
                  >
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full">
                      <GiftIcon
                        className="h-6 w-6 text-gray-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h1"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        Earn 15% or up to $15,000 over 12 months
                        <br />
                        for every new company you refer
                      </Dialog.Title>
                      {/* <div className="mt-4">
                        <p className="text-sm text-gray-500">
                          Invite someone who can benefit from nearly-free money
                          and they'll earn a $500 credit when they
                          become a member.
                          <br />
                          <br />
                          Some referrees earn over $5,000 / mo.
                        </p>
                      </div> */}
                      <br />
                      <div className="text-left mt-2">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Invite with email
                        </label>
                      </div>
                      <div className="mt-2">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          onChange={handleEmailChange}
                          autoComplete="email"
                          required
                          value={email}
                          placeholder="yourfriend@company.com"
                          className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus-visible:ring-${emailStatus === 'incorrect' ? 'red' : 'indigo'
                            }-600 sm:text-sm sm:leading-6`}
                        />
                        <p className="text-red-800 ml-1 mb-2 text-sm">
                          {emailStatus === 'incorrect'
                            ? 'Invalid email, please try again'
                            : ''}
                        </p>
                      </div>
                      <div className="mt-2 text-left">
                        <label
                          htmlFor="comment"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Include a message (optional)
                        </label>
                        <div>
                          <textarea
                            rows={6}
                            name="comment"
                            id="comment"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            defaultValue="Hi there,

                                I'd like to share a way to find money you didn't know about before, it's called Upgraded. They work entirely on a contingency basis.
                                
                                They do a give-get program so that we each get $500 off.
                                
                                They've done over $10m in grants, vendor savings and automations at an 80% success rate and your report shows you have over $100,000 in savings.
                                
                                Kindly see this link to your report below.
                                
                                Hope you're doing well!"
                          />
                        </div>
                      </div>
                      <div className="text-left mt-2">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Savings Report Link (appended to your email)
                        </label>
                        <div>
                          <input
                            type="text"
                            name="reportLink"
                            id="reportLink"
                            disabled
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200 sm:text-sm sm:leading-6"
                            placeholder={`https://app.getupgraded.ca/grants-section?website=${domain}`}
                            value={`https://app.getupgraded.ca/grants-section?website=${domain}`}
                            aria-describedby="email-description"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-6">
                      <button
                        type="submit"
                        disabled={emailStatus !== 'correct'}
                        className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 cursor-pointer"
                        onClick={() => {
                          setDomain('');
                          setInviteOpen(false);
                        }}
                      >
                        Send
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <Notification
        showNotif={emailModalNotif.showNotif}
        setShowNotif={(val) => setShowNotif(val, setEmailModalNotif)}
        contents={emailModalNotif.contents}
      />
      <Notification
        showNotif={submitEnrichNotif.showNotif}
        setShowNotif={(val) => setShowNotif(val, setSubmitEnrichNotif)}
        contents={submitEnrichNotif.contents}
      />

      {/* Command Palette */}
      <ShortcutsModal
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        sections={shortcutSections}
      />

      <CommandPalette
        isOpen={commandPaletteOpen}
        admin={admin}
        onClose={() => {
          debugLog('Layout: CommandPalette onClose called, setting commandPaletteOpen to false');
          setIsClosing(true);
          setCommandPaletteOpen(false);
          // Reset closing state after a brief delay
          setTimeout(() => setIsClosing(false), 100);
        }}
      />

      {/* Company profile search — pick company, then fields / Grant Q&A */}
      <CompanyProfileSearchPalette
        isOpen={companyProfileSearchOpen}
        onClose={() => setCompanyProfileSearchOpen(false)}
        company={company}
        sectionNavItems={companyProfileSectionNavItems}
        allowCompanyPick={admin}
        onSelect={handleCompanyProfileSearchSelect}
      />
      
      {/* Add Grant Modal */}
      {admin && (
        <AddGrantModal
          isOpen={isAddGrantModalOpen}
          onClose={() => setIsAddGrantModalOpen(false)}
        />
      )}

      <GrantOpportunityModal
        isOpen={opportunityModal.open}
        onClose={closeOpportunityModal}
        mode={opportunityModal.mode}
        opportunity={opportunityModal.opportunity}
        formData={opportunityModal.formData}
        onFormDataChange={updateOpportunityFormData}
        onSave={handleOpportunityModalSave}
        allGrants={opportunityModalGrants}
        allCompanies={opportunityModalCompanies}
        selectedGrant={opportunityModal.selectedGrant}
        selectedCompany={opportunityModalSelectedCompany}
        onSelectedGrantChange={handleOpportunitySelectedGrantChange}
        onSelectedCompanyChange={handleOpportunitySelectedCompanyChange}
        currencyFormat={opportunityCurrencyFormat}
        statusOptions={OPPORTUNITY_STATUS_OPTIONS}
        isAllCompaniesMode={admin}
      />

      {/* Feedback Modal */}
      <Transition.Root show={isFeedbackModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsFeedbackModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setIsFeedbackModalOpen(false)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                        Feedback
                      </Dialog.Title>
                      
                      <div className="mt-2">
                        <textarea
                          rows={4}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                          placeholder="Ideas to improve your experience..."
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          onKeyDown={(e) => {
                            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                              e.preventDefault();
                              if (feedbackText.trim() && !isSubmittingFeedback) {
                                handleFeedbackSubmit();
                              }
                            }
                          }}
                          autoFocus
                        />
                      </div>

                      <div className="mt-4 text-sm text-gray-500">
                        Need help?{' '}
                        <a href="#" className="text-blue-600 hover:text-blue-500" onClick={(e) => { e.preventDefault(); }}>
                          Contact us
                        </a>
                        {' '}or{' '}
                        <a href="#" className="text-blue-600 hover:text-blue-500" onClick={(e) => { e.preventDefault(); }}>
                          see docs
                        </a>
                      </div>

                      <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                        <button
                          type="button"
                          className="inline-flex w-full justify-center rounded-md bg-gray-800 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleFeedbackSubmit}
                          disabled={!feedbackText.trim() || isSubmittingFeedback}
                        >
                          {isSubmittingFeedback ? 'Sending...' : 'Send'}
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400">
                            <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">⌘</kbd>
                            <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">↵</kbd>
                          </span>
                        </button>
                        <button
                          type="button"
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                          onClick={() => setIsFeedbackModalOpen(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default Layout;
